import React, { useEffect, useState, useCallback, useMemo, lazy, Suspense } from 'react';
import '../styles/HomePage.css';
import Plot from 'react-plotly.js';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import debounce from 'lodash/debounce';
import dataJson from '../data/data.json'; // 新增：导入数据文件

const PrettoSlider = styled(Slider)({
  color: '#52af77',
  height: 8,
  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    height: 20,
    width: 20,
    backgroundColor: '#fff',
    border: '2px solid currentColor',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&:before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    lineHeight: 1.2,
    fontSize: 12,
    background: 'unset',
    padding: 0,
    width: 28,
    height: 28,
    borderRadius: '50% 50% 50% 0',
    backgroundColor: '#52af77',
    transformOrigin: 'bottom left',
    transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
    '&:before': { display: 'none' },
    '&.MuiSlider-valueLabelOpen': {
      transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
    },
    '& > *': {
      transform: 'rotate(45deg)',
    },
  },
});

const MAIN_ELEMENT_GROUPS = [
  'Mg',
  'Multi',
  'Fe',
  'Cu',
  'Al',
  'Ti',
  'Ni',
  'Other (Minor)'
];

const PIE_DISPLAY_MODES = {
  DEFAULT: 'default',
  USER: 'user',
  COMBINED: 'combined',
};

const getMainElementGroup = (mainElem) => {
  const normalized = (mainElem || '').trim();
  if (['Mg', 'Multi', 'Fe', 'Cu', 'Al', 'Ti', 'Ni'].includes(normalized)) {
    return normalized;
  }
  return 'Other (Minor)';
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-tabpanel-${index}`}
      aria-labelledby={`data-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// 懒加载 ElementCorrelationTab 组件
const ElementCorrelationTab = lazy(() => import('./tabs/ElementCorrelationTab'));
// 懒加载 ElementCooccurrenceTab 组件
const ElementCooccurrenceTab = lazy(() => import('./tabs/ElementCooccurrenceTab'));
// 懒加载 FeatureAnalysisTab 组件
const FeatureAnalysisTab = lazy(() => import('./tabs/FeatureAnalysisTab'));
// 懒加载 SimulationDataTab 组件
const SimulationDataTab = lazy(() => import('./tabs/SimulationDataTab'));

function DataPage({ onNavigate, defaultTabIndex = 0 }) {
  const [tabValue, setTabValue] = useState(defaultTabIndex);
  const [baseData, setBaseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userUploadData, setUserUploadData] = useState([]);
  const [dataDisplaySource, setDataDisplaySource] = useState(PIE_DISPLAY_MODES.DEFAULT);
  const [dataUploadError, setDataUploadError] = useState('');
  const [dataUploadName, setDataUploadName] = useState('');
  const [showDataFormat, setShowDataFormat] = useState(false);

  // 当defaultTabIndex改变时更新tabValue
  useEffect(() => {
    if (defaultTabIndex !== undefined) {
      setTabValue(defaultTabIndex);
    }
  }, [defaultTabIndex]);
  const [selectedMainGroups, setSelectedMainGroups] = useState([]);
  const [availableMainGroups, setAvailableMainGroups] = useState([]);
  const [elementDisplayMode, setElementDisplayMode] = useState('percentage'); // 'percentage' or 'count'
  const [elementDistributionUnit, setElementDistributionUnit] = useState('at'); // 'at' for atomic%, 'wt' for weight%
  const [elementAnalysisUnit, setElementAnalysisUnit] = useState('at'); // 'at' or 'wt' for element analysis
  const [ranges, setRanges] = useState({
    yieldStrength: [0, 1000],
    ultimateStrength: [0, 1000],
    elongation: [0, 100],
    density: [0, 10],
    avg_strength: [0, 1000]
  });
  const [axes, setAxes] = useState({
    x: 'density',
    y: 'avg_strength',
    size: 'fixed'
  });
  const [selectedProperty, setSelectedProperty] = useState('yield_strength'); // 新增：选中的性能指标
  const [elementFrequencyRange, setElementFrequencyRange] = useState([30, 100]);
  const [correlationMethod, setCorrelationMethod] = useState('pearson');
  const [propertyRanges, setPropertyRanges] = useState({
    yield_strength: [0, 1000],
    ultimate_strength: [0, 1000],
    elongation: [0, 100],
    density: [0, 10]
  });
  const [correlationMatrix, setCorrelationMatrix] = useState([]);
  const [correlationElements, setCorrelationElements] = useState([]);
  const [elementCounts, setElementCounts] = useState({});
  const [propertyCorrelationMatrix, setPropertyCorrelationMatrix] = useState([]); // 新增：性能相关性矩阵
  const [propertyCorrelationElements, setPropertyCorrelationElements] = useState([]); // 新增：性能相关性元素列表
  const [elementFrequencyRangeInitialized, setElementFrequencyRangeInitialized] = useState(false);
  const [propertyAnalysisMode, setPropertyAnalysisMode] = useState('weight'); // 新增：Element-Property分析模式
  
  const data = useMemo(() => {
    if (dataDisplaySource === PIE_DISPLAY_MODES.USER) {
      return userUploadData;
    }
    if (dataDisplaySource === PIE_DISPLAY_MODES.COMBINED) {
      return [...baseData, ...userUploadData];
    }
    return baseData;
  }, [baseData, userUploadData, dataDisplaySource]);

  // 元素分析相关状态
  const [elementAnalysisElement, setElementAnalysisElement] = useState('Fe'); // 选中的元素
  const [elementAnalysisProperty, setElementAnalysisProperty] = useState('yield_strength'); // 选中的性能指标
  
  // 原子量数据（常见元素）
  const atomicWeights = useMemo(() => ({
    'H': 1.008, 'He': 4.003, 'Li': 6.941, 'Be': 9.012, 'B': 10.811, 'C': 12.011, 'N': 14.007, 'O': 15.999,
    'F': 18.998, 'Ne': 20.180, 'Na': 22.990, 'Mg': 24.305, 'Al': 26.982, 'Si': 28.086, 'P': 30.974, 'S': 32.065,
    'Cl': 35.453, 'Ar': 39.948, 'K': 39.098, 'Ca': 40.078, 'Sc': 44.956, 'Ti': 47.867, 'V': 50.942, 'Cr': 51.996,
    'Mn': 54.938, 'Fe': 55.845, 'Co': 58.933, 'Ni': 58.693, 'Cu': 63.546, 'Zn': 65.409, 'Ga': 69.723, 'Ge': 72.640,
    'As': 74.922, 'Se': 78.096, 'Br': 79.904, 'Kr': 83.798, 'Rb': 85.468, 'Sr': 87.620, 'Y': 88.906, 'Zr': 91.224,
    'Nb': 92.906, 'Mo': 95.940, 'Tc': 98.000, 'Ru': 101.070, 'Rh': 102.906, 'Pd': 106.420, 'Ag': 107.868, 'Cd': 112.411,
    'In': 114.818, 'Sn': 118.710, 'Sb': 121.760, 'Te': 127.600, 'I': 126.904, 'Xe': 131.293, 'Cs': 132.905, 'Ba': 137.327,
    'La': 138.905, 'Ce': 140.116, 'Pr': 140.908, 'Nd': 144.242, 'Pm': 145.000, 'Sm': 150.360, 'Eu': 151.964, 'Gd': 157.250,
    'Tb': 158.925, 'Dy': 162.500, 'Ho': 164.930, 'Er': 167.259, 'Tm': 168.934, 'Yb': 173.040, 'Lu': 174.967, 'Hf': 178.490,
    'Ta': 180.948, 'W': 183.840, 'Re': 186.207, 'Os': 190.230, 'Ir': 192.217, 'Pt': 195.084, 'Au': 196.967, 'Hg': 200.590,
    'Tl': 204.383, 'Pb': 207.200, 'Bi': 208.980, 'Po': 209.000, 'At': 210.000, 'Rn': 222.000, 'Fr': 223.000, 'Ra': 226.000,
    'Ac': 227.000, 'Th': 232.038, 'Pa': 231.036, 'U': 238.029
  }), []);
  
  // 将原子百分比转换为重量百分比
  const convertAtomicToWeight = useCallback((composition) => {
    if (!composition) return {};
    
    // 计算总的原子权重
    let totalWeight = 0;
    const elementWeights = {};
    
    Object.entries(composition).forEach(([element, atomicPercent]) => {
      const atomicWeight = atomicWeights[element] || 55.845; // 默认使用Fe的原子量
      const weight = atomicPercent * atomicWeight;
      elementWeights[element] = weight;
      totalWeight += weight;
    });
    
    // 转换为重量百分比
    const weightComposition = {};
    Object.entries(elementWeights).forEach(([element, weight]) => {
      weightComposition[element] = (weight / totalWeight) * 100;
    });
    
    return weightComposition;
  }, [atomicWeights]);

  const parseUploadCsvContent = useCallback((text) => {
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (!lines.length) return [];

    const headers = lines[0].split(',').map((h) => h.trim());
    const normalizedHeaders = headers.map((h) => h.toLowerCase().replace(/[\s_-]+/g, ''));
    const getIndex = (candidates) => normalizedHeaders.findIndex((h) => candidates.includes(h));
    const mainElemIdx = getIndex(['mainelem', 'mainelement']);
    const mainGroupIdx = getIndex(['mainelementgroup', 'maingroup']);

    const compositionIdx = getIndex(['composition']);
    const requiredNumericColumns = {
      yield_strength: ['yieldstrength', 'ys'],
      ultimate_strength: ['ultimatestrength', 'uts'],
      elongation: ['elongation', 'ductility'],
      density: ['density'],
      avg_strength: ['avgstrength', 'averagestrength']
    };
    const optionalColumns = {
      ultimate_strength_compression: ['ultimatestrengthcompression', 'utscompression'],
      bm: ['bm'],
      em: ['em'],
      process_condition: ['processcondition', 'process'],
      test_condition: ['testcondition', 'test'],
      source: ['source']
    };
    const numericColumnIndexes = {};
    Object.entries(requiredNumericColumns).forEach(([key, candidates]) => {
      numericColumnIndexes[key] = getIndex(candidates);
    });
    const optionalColumnIndexes = {};
    Object.entries(optionalColumns).forEach(([key, candidates]) => {
      optionalColumnIndexes[key] = getIndex(candidates);
    });
    const elementColumns = headers
      .map((header, idx) => ({ header, idx }))
      .filter(({ header, idx }) => ![mainElemIdx, mainGroupIdx].includes(idx) && /^[A-Z][a-z]?$/.test(header.trim()));

    if (compositionIdx === -1 && elementColumns.length === 0) {
      throw new Error('CSV must include composition JSON or element columns (e.g., Fe, Ni) for alloy composition.');
    }

    const availablePropertyColumns = Object.entries(numericColumnIndexes)
      .filter(([, idx]) => idx !== -1)
      .map(([key]) => key);
    if (availablePropertyColumns.length === 0) {
      throw new Error('CSV must include at least one property column (e.g., yield_strength, ultimate_strength, elongation, density, avg_strength).');
    }

    const rows = lines.slice(1);
    const parsed = rows
      .map((line) => {
        const cells = line.split(',').map((c) => c.trim());
        const rawMainElem = mainElemIdx !== -1 ? cells[mainElemIdx] : '';
        const rawGroupValue = mainGroupIdx !== -1 ? cells[mainGroupIdx] : '';
        const entry = {};

        Object.entries(optionalColumnIndexes).forEach(([key, idx]) => {
          if (idx === -1) return;
          const value = cells[idx];
          if (!value) return;
          const num = Number(value);
          entry[key] = Number.isFinite(num) ? num : value;
        });

        const composition = {};

        if (compositionIdx !== -1 && cells[compositionIdx]) {
          try {
            const parsedComp = JSON.parse(cells[compositionIdx]);
            if (parsedComp && typeof parsedComp === 'object') {
              Object.entries(parsedComp).forEach(([key, value]) => {
                const num = Number(value);
                if (Number.isFinite(num)) {
                  composition[key.trim()] = num;
                }
              });
            }
          } catch (err) {
            // ignore invalid JSON in composition column
          }
        }

        elementColumns.forEach(({ header, idx }) => {
          const num = Number(cells[idx]);
          if (Number.isFinite(num)) {
            composition[header.trim()] = num;
          }
        });

        if (!Object.keys(composition).length) return null;
        entry.composition = composition;

        const parsedMainElem = rawMainElem || rawGroupValue;
        if (parsedMainElem) {
          entry.main_elem = parsedMainElem;
          entry.main_element_group = getMainElementGroup(parsedMainElem);
        } else {
          const topElement = Object.entries(composition)
            .filter(([, value]) => Number.isFinite(value))
            .sort((a, b) => b[1] - a[1])[0]?.[0];
          if (!topElement) return null;
          entry.main_elem = topElement;
          entry.main_element_group = getMainElementGroup(topElement);
        }

        let hasAnyProperty = false;
        Object.entries(numericColumnIndexes).forEach(([key, idx]) => {
          if (idx === -1) return;
          const rawValue = cells[idx];
          if (rawValue === undefined || rawValue === '') return;
          const num = Number(rawValue);
          if (!Number.isFinite(num)) return;
          entry[key] = num;
          hasAnyProperty = true;
        });
        if (!hasAnyProperty) return null;

        return entry;
      })
      .filter(Boolean);

    return parsed;
  }, []);

  const handleDataCsvUpload = useCallback(
    (file) => {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setDataUploadError('Please upload a .csv file.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = String(e.target?.result || '');
          const parsed = parseUploadCsvContent(text);
          if (!parsed.length) {
            throw new Error('No valid rows found in CSV.');
          }
          setUserUploadData(parsed);
          setDataUploadName(file.name);
          setDataUploadError('');
          setDataDisplaySource(PIE_DISPLAY_MODES.USER);
        } catch (err) {
          setDataUploadError(err.message || 'Failed to parse CSV file.');
        }
      };
      reader.onerror = () => setDataUploadError('Failed to read the uploaded file.');
      reader.readAsText(file);
    },
    [parseUploadCsvContent],
  );

  // 将 axisOptions 包装在 useMemo 中
  const axisOptions = useMemo(() => [
    { value: 'density', label: 'Density (g/cm³)', unit: 'g/cm³' },
    { value: 'yield_strength', label: 'Yield Strength (MPa)', unit: 'MPa' },
    { value: 'ultimate_strength', label: 'Ultimate Strength (MPa)', unit: 'MPa' },
    { value: 'avg_strength', label: 'Average Strength (MPa)', unit: 'MPa' },
    { value: 'elongation', label: 'Elongation (%)', unit: '%' },
    { value: 'strength_density_ratio', label: 'Ultimate Strength/Density (MPa·cm³/g)', unit: 'MPa·cm³/g' },
    { value: 'yield_strength_density_ratio', label: 'Yield Strength/Density (MPa·cm³/g)', unit: 'MPa·cm³/g' },
    { value: 'yield_strength_elongation', label: 'Yield Strength × Elongation (MPa·%)', unit: 'MPa·%' },
    { value: 'ultimate_strength_elongation', label: 'Ultimate Strength × Elongation (MPa·%)', unit: 'MPa·%' },
    { value: 'fixed', label: 'Fixed Size', unit: '' }
  ], []);

  // 属性选项
  const propertyOptions = useMemo(() => [
    { value: 'density', label: 'Density (g/cm³)', unit: 'g/cm³' },
    { value: 'avg_strength', label: 'Average Strength (MPa)', unit: 'MPa' },
    { value: 'yield_strength', label: 'Yield Strength (MPa)', unit: 'MPa' },
    { value: 'ultimate_strength', label: 'Ultimate Strength (MPa)', unit: 'MPa' },
    { value: 'elongation', label: 'Elongation (%)', unit: '%' }
  ], []);

  // 元素分析的性能选项（包含比值）
  const elementAnalysisPropertyOptions = useMemo(() => [
    { value: 'yield_strength', label: 'Yield Strength (MPa)', unit: 'MPa' },
    { value: 'ultimate_strength', label: 'Ultimate Strength (MPa)', unit: 'MPa' },
    { value: 'elongation', label: 'Elongation (%)', unit: '%' },
    { value: 'density', label: 'Density (g/cm³)', unit: 'g/cm³' },
    { value: 'yield_strength_density_ratio', label: 'Yield Strength/Density (MPa·cm³/g)', unit: 'MPa·cm³/g' },
    { value: 'ultimate_strength_density_ratio', label: 'Ultimate Strength/Density (MPa·cm³/g)', unit: 'MPa·cm³/g' },
    { value: 'yield_strength_elongation', label: 'Yield Strength × Elongation (MPa·%)', unit: 'MPa·%' },
    { value: 'ultimate_strength_elongation', label: 'Ultimate Strength × Elongation (MPa·%)', unit: 'MPa·%' }
  ], []);

  // 获取当前轴的显示单位
  const getAxisUnit = (axis) => {
    return axisOptions.find(opt => opt.value === axis)?.unit || '';
  };

  // 获取当前轴的数据范围
  const getAxisRange = (axis) => {
    if (!data.length) return [0, 100];
    // 只考虑选中数据源的数据
    const filteredData = data.filter(d => selectedMainGroups.includes(d.main_element_group));
    if (!filteredData.length) return [0, 100];
    let values;
    if (axis === 'strength_density_ratio') {
      values = filteredData
        .map(d => (d.ultimate_strength && d.density && d.density > 0) ? d.ultimate_strength / d.density : null)
        .filter(v => typeof v === 'number' && isFinite(v));
    } else if (axis === 'yield_strength_density_ratio') {
      values = filteredData
        .map(d => (d.yield_strength && d.density && d.density > 0) ? d.yield_strength / d.density : null)
        .filter(v => typeof v === 'number' && isFinite(v));
    } else if (axis === 'yield_strength_elongation') {
      values = filteredData
        .map(d => (d.yield_strength && d.elongation) ? d.yield_strength * d.elongation : null)
        .filter(v => typeof v === 'number' && isFinite(v));
    } else if (axis === 'ultimate_strength_elongation') {
      values = filteredData
        .map(d => (d.ultimate_strength && d.elongation) ? d.ultimate_strength * d.elongation : null)
        .filter(v => typeof v === 'number' && isFinite(v));
    } else {
      values = filteredData.map(d => d[axis]).filter(v => typeof v === 'number' && isFinite(v));
    }
    if (!values.length) return [0, 100];
    return [
      Math.floor(Math.min(...values)),
      Math.ceil(Math.max(...values))
    ];
  };

  // 获取当前轴的范围值
  const getAxisValue = (axis) => {
    return ranges[axis] || getAxisRange(axis);
  };

  // 处理轴范围变化
  const handleAxisRangeChange = (axis, newValue) => {
    setRanges(prev => ({ ...prev, [axis]: newValue }));
  };

  // 处理轴选择变化
  const handleAxisChange = (axisType, value) => {
    setAxes(prev => ({ ...prev, [axisType]: value }));
    // 当切换轴时，重置该轴的范围为全范围
    const fullRange = getAxisRange(value);
    setRanges(prev => ({ ...prev, [value]: fullRange }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    // 直接使用导入的数据，无需fetch
    const json = dataJson;
    const enhancedData = json.map(item => ({
      ...item,
      main_element_group: getMainElementGroup(item.main_elem)
    }));
    setBaseData(enhancedData);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!data.length) {
      setAvailableMainGroups([]);
      setSelectedMainGroups([]);
      return;
    }
    const groups = MAIN_ELEMENT_GROUPS.filter(group =>
      data.some(d => d.main_element_group === group)
    );
    setAvailableMainGroups(groups);
    setSelectedMainGroups(prev => {
      const nextGroups = prev.filter(group => groups.includes(group));
      const effectiveGroups = nextGroups.length ? nextGroups : groups;
      updateRangesForSelectedSources(effectiveGroups, data);
      return effectiveGroups;
    });
  }, [data]);

  // 处理主元素分组选择变化
  const handleMainGroupChange = (group) => {
    setSelectedMainGroups(prev => {
      const newGroups = prev.includes(group)
        ? prev.filter(s => s !== group)
        : [...prev, group];
      
      // 更新选中分组后的范围
      updateRangesForSelectedSources(newGroups);
      
      return newGroups;
    });
  };

  // 处理全选/取消全选
  const handleSelectAllGroups = (event) => {
    const newGroups = event.target.checked ? availableMainGroups : [];
    setSelectedMainGroups(newGroups);
    // 更新选中分组后的范围
    updateRangesForSelectedSources(newGroups);
  };

  // 根据选中的主元素分组更新范围
  const updateRangesForSelectedSources = (groups, sourceData = data) => {
    if (!sourceData.length) return;

    // 获取选中分组的数据
    const filteredData = sourceData.filter(d => groups.includes(d.main_element_group));
    if (!filteredData.length) return;

    const getNumericRange = (items, key, step = 1) => {
      const values = items
        .map(item => item[key])
        .filter(v => typeof v === 'number' && isFinite(v));
      if (!values.length) return [0, 100];
      if (step === 0.1) {
        return [
          Math.floor(Math.min(...values) * 10) / 10,
          Math.ceil(Math.max(...values) * 10) / 10
        ];
      }
      return [
        Math.floor(Math.min(...values)),
        Math.ceil(Math.max(...values))
      ];
    };

    // 计算新的范围
    const newRanges = {
      yieldStrength: getNumericRange(filteredData, 'yield_strength'),
      ultimateStrength: getNumericRange(filteredData, 'ultimate_strength'),
      elongation: getNumericRange(filteredData, 'elongation'),
      density: getNumericRange(filteredData, 'density', 0.1),
      avg_strength: getNumericRange(filteredData, 'avg_strength')
    };

    setRanges(newRanges);
  };

  // 过滤数据
  const filtered = data.filter(d => {
    // 基础过滤条件
    const baseFilter = 
      d.yield_strength >= ranges.yieldStrength[0] && 
      d.yield_strength <= ranges.yieldStrength[1] &&
      d.ultimate_strength >= ranges.ultimateStrength[0] && 
      d.ultimate_strength <= ranges.ultimateStrength[1] &&
      d.elongation >= ranges.elongation[0] && 
      d.elongation <= ranges.elongation[1] &&
      selectedMainGroups.includes(d.main_element_group);

    // 只在需要时计算强度密度比
    const getValue = (axis, datum) => {
      if (axis === 'strength_density_ratio') {
        return (datum.ultimate_strength && datum.density && datum.density > 0)
          ? datum.ultimate_strength / datum.density
          : null;
      } else if (axis === 'yield_strength_density_ratio') {
        return (datum.yield_strength && datum.density && datum.density > 0)
          ? datum.yield_strength / datum.density
          : null;
      } else if (axis === 'yield_strength_elongation') {
        return (datum.yield_strength && datum.elongation)
          ? datum.yield_strength * datum.elongation
          : null;
      } else if (axis === 'ultimate_strength_elongation') {
        return (datum.ultimate_strength && datum.elongation)
          ? datum.ultimate_strength * datum.elongation
          : null;
      }
      return datum[axis];
    };
    // 过滤掉无效比值
    if ((axes.x === 'strength_density_ratio' && getValue(axes.x, d) == null) ||
        (axes.y === 'strength_density_ratio' && getValue(axes.y, d) == null) ||
        (axes.size === 'strength_density_ratio' && getValue(axes.size, d) == null) ||
        (axes.x === 'yield_strength_density_ratio' && getValue(axes.x, d) == null) ||
        (axes.y === 'yield_strength_density_ratio' && getValue(axes.y, d) == null) ||
        (axes.size === 'yield_strength_density_ratio' && getValue(axes.size, d) == null) ||
        (axes.x === 'yield_strength_elongation' && getValue(axes.x, d) == null) ||
        (axes.y === 'yield_strength_elongation' && getValue(axes.y, d) == null) ||
        (axes.size === 'yield_strength_elongation' && getValue(axes.size, d) == null) ||
        (axes.x === 'ultimate_strength_elongation' && getValue(axes.x, d) == null) ||
        (axes.y === 'ultimate_strength_elongation' && getValue(axes.y, d) == null) ||
        (axes.size === 'ultimate_strength_elongation' && getValue(axes.size, d) == null)) {
      return false;
    }
    const axisFilter = 
      getValue(axes.x, d) >= getAxisValue(axes.x)[0] &&
      getValue(axes.x, d) <= getAxisValue(axes.x)[1] &&
      getValue(axes.y, d) >= getAxisValue(axes.y)[0] &&
      getValue(axes.y, d) <= getAxisValue(axes.y)[1];
    const sizeFilter = axes.size === 'fixed' ? true :
      getValue(axes.size, d) >= getAxisValue(axes.size)[0] &&
      getValue(axes.size, d) <= getAxisValue(axes.size)[1];
    return baseFilter && axisFilter && sizeFilter;
  });

  // 获取所有主元素类别
  const allMainElems = Array.from(new Set(filtered.map(d => d.main_elem)));
  // 预计算全局的 size 轴最大值，用于一致的 sizeref，避免某些分组被放大
  const getValueForSize = (datum) => {
    if (axes.size === 'strength_density_ratio') {
      return (datum.ultimate_strength && datum.density && datum.density > 0) 
        ? datum.ultimate_strength / datum.density : null;
    } else if (axes.size === 'yield_strength_density_ratio') {
      return (datum.yield_strength && datum.density && datum.density > 0) 
        ? datum.yield_strength / datum.density : null;
    } else if (axes.size === 'yield_strength_elongation') {
      return (datum.yield_strength && datum.elongation) 
        ? datum.yield_strength * datum.elongation : null;
    } else if (axes.size === 'ultimate_strength_elongation') {
      return (datum.ultimate_strength && datum.elongation) 
        ? datum.ultimate_strength * datum.elongation : null;
    } else if (axes.size === 'fixed') {
      return null;
    }
    return datum[axes.size];
  };
  const globalSizeValues = axes.size === 'fixed'
    ? []
    : filtered
        .map(getValueForSize)
        .filter(v => v !== null && v !== undefined);
  const globalMaxSize = axes.size === 'fixed'
    ? 1
    : Math.max(...globalSizeValues, 1);
  // 颜色序列（与scatter.py保持一致，12色）
  const pastelColors = [
    '#AEC6CF', '#FFB347', '#B39EB5', '#77DD77', '#FF6961', '#FDFD96',
    '#CFCFC4', '#836953', '#03C03C', '#779ECB', '#966FD6', '#F49AC2'
  ];

  // 饼图数据悬浮模板
  const pieHoverTemplate = '%{label}: %{percent}<br>Count: %{value}<extra></extra>';

  // 格式化composition数据为可读字符串
  const formatComposition = (composition) => {
    if (!composition) return 'N/A';
    return 'Available after publication';
  };

  // 构建每个点的 hover 文本，只展示非空属性
  const buildHoverText = (datum, mainElement) => {
    const lines = [`<b>Main Element:</b> ${mainElement}`];
    const fields = [
      { key: 'density', label: 'Density', unit: 'g/cm³' },
      { key: 'avg_strength', label: 'Average Strength', unit: 'MPa' },
      { key: 'elongation', label: 'Elongation', unit: '%' },
      { key: 'yield_strength', label: 'Yield Strength', unit: 'MPa' },
      { key: 'ultimate_strength', label: 'Ultimate Strength', unit: 'MPa' },
      { key: 'ultimate_strength_compression', label: 'Ultimate Strength (Compression)', unit: 'MPa' },
      { key: 'bm', label: 'BM' },
      { key: 'em', label: 'EM' },
      { key: 'strength_density_ratio', label: 'Ultimate Strength/Density', unit: 'MPa·cm³/g' },
      { key: 'yield_strength_density_ratio', label: 'Yield Strength/Density', unit: 'MPa·cm³/g' },
      { key: 'yield_strength_elongation', label: 'Yield Strength × Elongation', unit: 'MPa·%' },
      { key: 'ultimate_strength_elongation', label: 'Ultimate Strength × Elongation', unit: 'MPa·%' },
    ];

    fields.forEach(({ key, label, unit }) => {
      const value = datum[key];
      if (value === null || value === undefined || value === '') return;

      const formatted =
        typeof value === 'number' && Number.isFinite(value)
          ? value.toFixed(2)
          : value;
      lines.push(`<b>${label}:</b> ${formatted}${unit ? ` ${unit}` : ''}`);
    });

    const compositionText = formatComposition(datum.composition);
    if (compositionText) {
      lines.push(`<b>Composition(at. %):</b> ${compositionText}`);
    }
    if (datum.cleaned_process_condition) {
      lines.push(`<b>Process Condition:</b> ${datum.cleaned_process_condition}`);
    }
    if (datum.test_condition) {
      lines.push(`<b>Test Condition:</b> ${datum.test_condition}`);
    }
    return `${lines.join('<br>')}<extra></extra>`;
  };

  // 清理process_condition数据，移除开头的问号
  const cleanProcessCondition = (processCondition) => {
    if (!processCondition) return 'N/A';
    // 如果以问号开头，移除问号并清理首字母前的空格
    return processCondition.startsWith('?') 
      ? processCondition.substring(1).trim() 
      : processCondition;
  };

  // 按主元素分组，每组一个trace
  const plotData = allMainElems.map((elem, i) => {
    const group = filtered.filter(d => d.main_elem === elem);
    const groupCustomData = group.map(d => ({
      ...d,
      strength_density_ratio: (d.ultimate_strength && d.density && d.density > 0) ? d.ultimate_strength / d.density : null,
      yield_strength_density_ratio: (d.yield_strength && d.density && d.density > 0) ? d.yield_strength / d.density : null,
      yield_strength_elongation: (d.yield_strength && d.elongation) ? d.yield_strength * d.elongation : null,
      ultimate_strength_elongation: (d.ultimate_strength && d.elongation) ? d.ultimate_strength * d.elongation : null,
      cleaned_process_condition: cleanProcessCondition(d.process_condition)
    }));
    const hoverTexts = groupCustomData.map(datum => buildHoverText(datum, elem));
    
    // 创建getValue函数用于计算复合指标
    const getValueForPlot = (axis, datum) => {
      if (axis === 'strength_density_ratio') {
        return (datum.ultimate_strength && datum.density && datum.density > 0) 
          ? datum.ultimate_strength / datum.density : null;
      } else if (axis === 'yield_strength_density_ratio') {
        return (datum.yield_strength && datum.density && datum.density > 0) 
          ? datum.yield_strength / datum.density : null;
      } else if (axis === 'yield_strength_elongation') {
        return (datum.yield_strength && datum.elongation) 
          ? datum.yield_strength * datum.elongation : null;
      } else if (axis === 'ultimate_strength_elongation') {
        return (datum.ultimate_strength && datum.elongation) 
          ? datum.ultimate_strength * datum.elongation : null;
      }
      return datum[axis];
    };
    // Ensure point sizes are never zero to keep markers visible even when the size metric is 0,
    // but keep the floor small to avoid oversized zero-elongation markers.
    const minDynamicSize = 1; // pixels (before sizeref scaling)
    const sizesForPlot = axes.size === 'fixed'
      ? Array(group.length).fill(10)
      : group.map(d => Math.max(getValueForPlot(axes.size, d) ?? 0, minDynamicSize));
    const targetMarkerPx = 18; // desired max marker diameter in pixels for the largest value
    const sizeRef = axes.size === 'fixed'
      ? 1
      : 2.0 * Math.max(globalMaxSize, minDynamicSize) / (targetMarkerPx ** 2);
    
    return {
      x: groupCustomData.map(d => getValueForPlot(axes.x, d)),
      y: groupCustomData.map(d => getValueForPlot(axes.y, d)),
      text: groupCustomData.map(d => formatComposition(d.composition)),
      customdata: groupCustomData,
      marker: {
        size: sizesForPlot,
        sizemode: axes.size === 'fixed' ? 'diameter' : 'area',
        sizeref: sizeRef,
        color: pastelColors[i % pastelColors.length],
        line: { width: 0.6, color: 'black' },
        opacity: 0.9
      },
      mode: 'markers',
      type: 'scatter',
      name: elem,
      hovertemplate: hoverTexts
    };
  });

  const layout = useMemo(() => ({
    title: {
      text: 'Alloy Performance Distribution',
      font: {
        size: 24,
        family: 'Arial, sans-serif'
      },
      y: 0.95
    },
    xaxis: { 
      title: {
        text: axisOptions.find(opt => opt.value === axes.x)?.label || 'X Axis',
        font: {
          size: 16,
          family: 'Arial, sans-serif'
        }
      },
      showgrid: true,
      gridcolor: '#E5E5E5',
      zeroline: false,
      showline: true,
      linewidth: 1,
      linecolor: '#000000',
      uirevision: 'x-axis'
    },
    yaxis: { 
      title: axisOptions.find(opt => opt.value === axes.y)?.label || 'Y Axis',
      showgrid: true,
      gridcolor: '#E5E5E5',
      zeroline: false,
      showline: true,
      linewidth: 1,
      linecolor: '#000000',
      uirevision: 'y-axis',
      tickfont: { size: 11 },
      automargin: true
    },
    hovermode: 'closest',
    template: 'simple_white',
    height: 700,
    margin: {
      l: 80,
      r: 120,
      t: 100,
      b: 80
    },
    legend: { 
      title: { 
        text: 'Main Element',
        font: {
          size: 16,
          family: 'Arial, sans-serif'
        }
      },
      orientation: 'v',
      x: 1.02,
      y: 1,
      traceorder: 'normal',
      itemsizing: 'constant'
    },
    plot_bgcolor: 'white',
    paper_bgcolor: 'white',
    showlegend: true,
    uirevision: 'scatter-plot'
  }), [axes.x, axes.y, axisOptions]);

  const getPieSourceData = useCallback(() => {
    return data.map((item) => ({
      main_element_group: item.main_element_group,
      composition: item.composition,
    }));
  }, [data]);

  // 计算饼图数据
  const calculatePieData = useCallback(() => {
    // 获取当前选中的数据
    const sourceData = getPieSourceData();
    const currentData = sourceData.filter(d => selectedMainGroups.includes(d.main_element_group));
    
    // 计算主元素分组分布
    const groupCounts = {};
    currentData.forEach(d => {
      groupCounts[d.main_element_group] = (groupCounts[d.main_element_group] || 0) + 1;
    });
    
    // 计算元素分布
    const elementFrequency = {};
    const elementCounts = {};
    
    currentData.forEach(d => {
      try {
        if (!d.composition) {
          console.log('Empty composition');
          return;
        }

        // 根据选择的单位计算元素含量
        let composition = d.composition;
        if (elementDistributionUnit === 'wt') {
          // 转换为重量百分比
          composition = convertAtomicToWeight(d.composition);
        }
        // 如果是 'at'，直接使用原始的原子百分比数据

        Object.entries(composition).forEach(([element, value]) => {
          if (!isNaN(value)) {
            elementFrequency[element] = (elementFrequency[element] || 0) + value;
            elementCounts[element] = (elementCounts[element] || 0) + 1;
          }
        });
      } catch (error) {
        console.error('Error processing composition:', d.composition, error);
      }
    });

    // 转换为饼图数据格式
    const orderedGroups = MAIN_ELEMENT_GROUPS.filter(group => groupCounts[group] > 0);
    const mainGroupPieData = [{
      values: orderedGroups.map(group => groupCounts[group]),
      labels: orderedGroups,
      type: 'pie',
      hole: 0.4,
      marker: {
        colors: pastelColors
      },
      textinfo: 'none',  // 移除外部文本标签
      textposition: 'none',
      automargin: true,
      hovertemplate: pieHoverTemplate
    }];

    // 处理元素数据，显示所有元素
    const sortedElements = Object.entries(elementFrequency)
      .sort(([, a], [, b]) => b - a);
    
    // 准备饼图数据
    const elementValues = elementDisplayMode === 'percentage' 
      ? sortedElements.map(([, value]) => value)
      : sortedElements.map(([element]) => elementCounts[element]);
    
    const elementLabels = sortedElements.map(([element]) => element);

    // 只有当有数据时才创建饼图
    const elementPieData = elementValues.length > 0 ? [{
      values: elementValues,
      labels: elementLabels,
      type: 'pie',
      hole: 0.4,
      marker: {
        colors: pastelColors
      },
      textinfo: 'none',  // 移除外部文本标签
      textposition: 'none',
      automargin: true,
      hovertemplate: pieHoverTemplate
    }] : [];

    return { mainGroupPieData, elementPieData };
  }, [convertAtomicToWeight, elementDisplayMode, elementDistributionUnit, getPieSourceData, selectedMainGroups]);

  const pieData = useMemo(() => calculatePieData(), [calculatePieData]);

  // 饼图布局配置
  const pieLayout = {
    autosize: true,
    height: null,
    width: null,
    margin: { t: 50, b: 50, l: 50, r: 50 },
    showlegend: true,
    legend: {
      orientation: 'h',
      y: -0.1,
      font: {
        size: 12
      }
    }
  };

  // 简单高斯核密度估计函数
  function kernelDensityEstimator(kernel, X) {
    return function(V) {
      return X.map(function(x) {
        return [x, V.reduce(function(sum, v) { return sum + kernel(x - v); }, 0) / V.length];
      });
    };
  }
  function kernelGaussian(scale) {
    return function(u) {
      return Math.exp(-0.5 * (u / scale) * (u / scale)) / (scale * Math.sqrt(2 * Math.PI));
    };
  }

  // 计算属性分布数据
  const calculateDistributionData = () => {
    const currentData = data.filter(d => selectedMainGroups.includes(d.main_element_group));
    const values = currentData.map(d => d[selectedProperty]).filter(v => typeof v === 'number' && !isNaN(v));
    if (values.length === 0) return [];

    // 计算直方图数据
    const histogramData = [{
      x: values,
      type: 'histogram',
      name: 'Histogram',
      opacity: 0.7,
      marker: {
        color: '#52af77',
        line: {
          color: 'white',
          width: 1
        }
      },
      histnorm: 'probability density',
      hovertemplate: 'Range: %{x}<br>Count: %{customdata[0]}<br>Probability Density: %{y:.4f}<extra></extra>',
      autobinx: true,
      customdata: [], // 稍后填充
      showlegend: true
    }];

    // 计算每个bin的数量，填充customdata
    // 需要先用Plotly的直方图分箱算法，或手动分箱
    // 这里用简单分箱
    const binCount = 20;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const binWidth = (max - min) / binCount;
    const counts = Array(binCount).fill(0);
    values.forEach(v => {
      let idx = Math.floor((v - min) / binWidth);
      if (idx >= binCount) idx = binCount - 1;
      counts[idx]++;
    });
    histogramData[0].xbins = {start: min, end: max, size: binWidth};
    histogramData[0].customdata = counts.map(c => [c]);

    // KDE曲线（红色）
    const steps = 100;
    const X = Array.from({length: steps}, (_, i) => min + (max - min) * i / (steps - 1));
    const std = Math.sqrt(values.reduce((a, b) => a + Math.pow(b - (values.reduce((x, y) => x + y, 0) / values.length), 2), 0) / values.length);
    const bandwidth = 1.06 * std * Math.pow(values.length, -1/5);
    const kde = kernelDensityEstimator(kernelGaussian(bandwidth), X)(values);
    const kdeData = [{
      x: kde.map(d => d[0]),
      y: kde.map(d => d[1]),
      type: 'scatter',
      mode: 'lines',
      name: 'KDE',
      line: {
        color: 'red',
        width: 2
      },
      yaxis: 'y',
      hovertemplate: 'Value: %{x}<br>Probability Density: %{y:.4f}<extra></extra>',
      showlegend: true
    }];

    return [...histogramData, ...kdeData];
  };

  // 属性分布图布局配置
  const distributionLayout = {
    title: {
      text: `Distribution of ${propertyOptions.find(opt => opt.value === selectedProperty)?.label}`,
      font: { 
        size: 18,
        family: 'Arial, sans-serif'
      }
    },
    xaxis: {
      title: {
        text: propertyOptions.find(opt => opt.value === selectedProperty)?.label,
        font: {
          size: 16,
          family: 'Arial, sans-serif'
        }
      },
      showgrid: true,
      gridcolor: '#E5E5E5'
    },
    yaxis: {
      title: {
        text: 'Count',
        font: {
          size: 16,
          family: 'Arial, sans-serif'
        }
      },
      showgrid: true,
      gridcolor: '#E5E5E5'
    },
    yaxis2: {
      title: {
        text: 'Probability Density',
        font: {
          size: 16,
          family: 'Arial, sans-serif'
        }
      },
      overlaying: 'y',
      side: 'right',
      showgrid: false
    },
    showlegend: true,
    legend: {
      orientation: 'h',
      y: -0.2,
      font: {
        size: 14,
        family: 'Arial, sans-serif'
      }
    },
    margin: { t: 50, b: 80, l: 60, r: 60 },
    plot_bgcolor: 'white',
    paper_bgcolor: 'white'
  };

  // ===== 元素分布分析 =====
  // 统计所有元素
  const allElements = React.useMemo(() => {
    const set = new Set();
    data.forEach(d => {
      if (d.composition) {
        Object.keys(d.composition).forEach(el => set.add(el));
      }
    });
    return Array.from(set).sort();
  }, [data]);

  // 元素选择
  const [selectedElement, setSelectedElement] = useState('Fe');
  useEffect(() => {
    if (allElements.length && !allElements.includes(selectedElement)) {
      setSelectedElement(allElements[0]);
    }
  }, [allElements, selectedElement]);

  // 计算元素分布数据
  const calculateElementDistributionData = () => {
    if (!selectedElement) return [];
    const currentData = data.filter(d => selectedMainGroups.includes(d.main_element_group));
    
    // 根据选择的单位获取元素含量值
    const values = currentData.map(d => {
      if (!d.composition || !d.composition[selectedElement]) return 0;
      
      if (elementAnalysisUnit === 'wt') {
        // 转换为重量百分比
        const weightComposition = convertAtomicToWeight(d.composition);
        return weightComposition[selectedElement] || 0;
      } else {
        // 使用原子百分比
        return d.composition[selectedElement] || 0;
      }
    }).filter(v => v > 0);
    
    if (values.length === 0) return [];
    
    // 直方图
    const histogramData = [{
      x: values,
      type: 'histogram',
      name: 'Histogram',
      opacity: 0.7,
      marker: {
        color: '#52af77',
        line: {
          color: 'white',
          width: 1
        }
      },
      histnorm: 'probability density',
      hovertemplate: 'Range: %{x}<br>Count: %{customdata[0]}<br>Probability Density: %{y:.4f}<extra></extra>',
      autobinx: true,
      customdata: [], // 稍后填充
      showlegend: true
    }];
    // 计算每个bin的数量，填充customdata
    const binCount = 20;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const binWidth = (max - min) / binCount;
    const counts = Array(binCount).fill(0);
    values.forEach(v => {
      let idx = Math.floor((v - min) / binWidth);
      if (idx >= binCount) idx = binCount - 1;
      counts[idx]++;
    });
    histogramData[0].xbins = {start: min, end: max, size: binWidth};
    histogramData[0].customdata = counts.map(c => [c]);

    // KDE曲线（红色）
    const steps = 100;
    const X = Array.from({length: steps}, (_, i) => min + (max - min) * i / (steps - 1));
    const std = Math.sqrt(values.reduce((a, b) => a + Math.pow(b - (values.reduce((x, y) => x + y, 0) / values.length), 2), 0) / values.length);
    const bandwidth = 1.06 * std * Math.pow(values.length, -1/5);
    const kde = kernelDensityEstimator(kernelGaussian(bandwidth), X)(values);
    const kdeData = [{
      x: kde.map(d => d[0]),
      y: kde.map(d => d[1]),
      type: 'scatter',
      mode: 'lines',
      name: 'KDE',
      line: {
        color: 'red',
        width: 2
      },
      yaxis: 'y',
      hovertemplate: 'Value: %{x}<br>Probability Density: %{y:.4f}<extra></extra>',
      showlegend: true
    }];
    return [...histogramData, ...kdeData];
  };

  // 元素分布图布局
  const elementDistributionLayout = {
    title: {
      text: `${selectedElement} Element Distribution (${elementAnalysisUnit === 'wt' ? 'Weight %' : 'Atomic %'})`,
      font: { size: 18, family: 'Arial, sans-serif' }
    },
    xaxis: {
      title: { 
        text: elementAnalysisUnit === 'wt' ? 'Weight Fraction (%)' : 'Atomic Fraction (%)', 
        font: { size: 16, family: 'Arial, sans-serif' } 
      },
      showgrid: true,
      gridcolor: '#E5E5E5'
    },
    yaxis: {
      title: { text: 'Density', font: { size: 16, family: 'Arial, sans-serif' } },
      showgrid: true,
      gridcolor: '#E5E5E5'
    },
    showlegend: true,
    legend: {
      orientation: 'h',
      y: -0.2,
      font: { size: 14, family: 'Arial, sans-serif' }
    },
    margin: { t: 50, b: 80, l: 60, r: 60 },
    plot_bgcolor: 'white',
    paper_bgcolor: 'white'
  };

  // 只用于X/Y轴的选项（不含Fixed Size）
  const axisXYOptions = axisOptions.filter(opt => opt.value !== 'fixed');

  // 计算元素分析数据
  const calculateElementAnalysisData = useCallback(() => {
    if (!elementAnalysisElement || !data.length) return [];

    // 筛选包含指定元素的合金数据
    const filteredData = data.filter(d => {
      return selectedMainGroups.includes(d.main_element_group) && 
             d.composition && 
             d.composition[elementAnalysisElement] && 
             d.composition[elementAnalysisElement] > 0;
    });

    if (filteredData.length === 0) return [];

    // 准备散点图数据
    const plotData = filteredData.map(d => {
      // 根据选择的单位获取元素含量
      let elementPercent;
      let weightComposition = {};
      
      if (elementAnalysisUnit === 'wt') {
        // 转换组成为重量百分比
        weightComposition = convertAtomicToWeight(d.composition);
        elementPercent = weightComposition[elementAnalysisElement] || 0;
      } else {
        // 使用原子百分比
        elementPercent = d.composition[elementAnalysisElement] || 0;
        weightComposition = convertAtomicToWeight(d.composition); // 仍然计算用于悬浮信息
      }

      // 计算Y轴属性值
      let yValue;
      switch (elementAnalysisProperty) {
        case 'yield_strength_density_ratio':
          yValue = (d.yield_strength && d.density && d.density > 0) ? d.yield_strength / d.density : null;
          break;
        case 'ultimate_strength_density_ratio':
          yValue = (d.ultimate_strength && d.density && d.density > 0) ? d.ultimate_strength / d.density : null;
          break;
        case 'yield_strength_elongation':
          yValue = (d.yield_strength && d.elongation) ? d.yield_strength * d.elongation : null;
          break;
        case 'ultimate_strength_elongation':
          yValue = (d.ultimate_strength && d.elongation) ? d.ultimate_strength * d.elongation : null;
          break;
        default:
          yValue = d[elementAnalysisProperty];
      }

      return {
        x: elementPercent,
        y: yValue,
        originalData: d,
        weightComposition: weightComposition,
        atomicPercent: d.composition[elementAnalysisElement] || 0
      };
    }).filter(point => point.y !== null && point.y !== undefined && !isNaN(point.y));

    return [{
      x: plotData.map(p => p.x),
      y: plotData.map(p => p.y),
      customdata: plotData.map(p => ({
        ...p.originalData,
        elementPercent: p.x,
        atomicPercent: p.atomicPercent,
        elementWeightPercent: p.weightComposition[elementAnalysisElement] || 0,
        yValue: p.y,
        // 格式化所有元素的组分信息
        allElementsAtomic: Object.entries(p.originalData.composition || {})
          .sort(([,a], [,b]) => b - a)
          .map(([el, at]) => `${el}: ${at.toFixed(1)}at%`)
          .join(', '),
        allElementsWeight: Object.entries(p.weightComposition)
          .sort(([,a], [,b]) => b - a)
          .map(([el, wt]) => `${el}: ${wt.toFixed(1)}wt%`)
          .join(', '),
        cleaned_process_condition: cleanProcessCondition(p.originalData.process_condition)
      })),
      mode: 'markers',
      type: 'scatter',
      marker: {
        size: 8,
        color: '#52af77',
        line: { width: 1, color: 'rgba(0,0,0,0.3)' },
        opacity: 0.8
      },
      name: `${elementAnalysisElement} Analysis`,
      hovertemplate:
        `<b>Selected Element:</b> ${elementAnalysisElement}<br>` +
        `<b>AT%:</b> Available after publication<br>` +
        `<b>WT%:</b> Available after publication<br>` +
        `<b>${elementAnalysisPropertyOptions.find(opt => opt.value === elementAnalysisProperty)?.label}:</b> %{customdata.yValue:.2f} ${elementAnalysisPropertyOptions.find(opt => opt.value === elementAnalysisProperty)?.unit}<br>` +
        '<b>All Elements (AT%):</b> Available after publication<br>' +
        '<b>All Elements (WT%):</b> Available after publication<br>' +
        '<b>Density:</b> %{customdata.density:.2f} g/cm³<br>' +
        '<b>Ultimate Strength:</b> %{customdata.ultimate_strength:.2f} MPa<br>' +
        '<b>Elongation:</b> %{customdata.elongation:.2f}%<br>' +
        '<b>Process Condition:</b> %{customdata.cleaned_process_condition}<br>' +
        '<b>Test Condition:</b> %{customdata.test_condition}<extra></extra>'
    }];
  }, [elementAnalysisElement, elementAnalysisProperty, elementAnalysisUnit, data, selectedMainGroups, convertAtomicToWeight, elementAnalysisPropertyOptions]);

  // 元素分析图表布局
  const elementAnalysisLayout = useMemo(() => ({
    title: {
      text: `${elementAnalysisElement} Content vs ${elementAnalysisPropertyOptions.find(opt => opt.value === elementAnalysisProperty)?.label}`,
      font: { size: 18, family: 'Arial, sans-serif' }
    },
    xaxis: {
      title: {
        text: `${elementAnalysisElement} ${elementAnalysisUnit === 'wt' ? 'Weight' : 'Atomic'} Percentage (%)`,
        font: { size: 16, family: 'Arial, sans-serif' }
      },
      showgrid: true,
      gridcolor: '#E5E5E5',
      zeroline: false,
      showline: true,
      linewidth: 1,
      linecolor: '#000000'
    },
    yaxis: {
      title: {
        text: elementAnalysisPropertyOptions.find(opt => opt.value === elementAnalysisProperty)?.label,
        font: { size: 16, family: 'Arial, sans-serif' }
      },
      showgrid: true,
      gridcolor: '#E5E5E5',
      zeroline: false,
      showline: true,
      linewidth: 1,
      linecolor: '#000000'
    },
    hovermode: 'closest',
    template: 'simple_white',
    height: 600,
    margin: { l: 80, r: 50, t: 80, b: 80 },
    plot_bgcolor: 'white',
    paper_bgcolor: 'white',
    showlegend: false
  }), [elementAnalysisElement, elementAnalysisProperty, elementAnalysisUnit, elementAnalysisPropertyOptions]);

  // 将 calculateCorrelation 相关的函数移到组件外部
  const calculatePearsonCorrelation = useCallback((x, y) => {
    const n = x.length;
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((a, b, i) => a + b * y[i], 0);
    const sumX2 = x.reduce((a, b) => a + b * b, 0);
    const sumY2 = y.reduce((a, b) => a + b * b, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    return denominator === 0 ? 0 : numerator / denominator;
  }, []);

  const getRanks = useCallback((arr) => {
    const sorted = [...arr].sort((a, b) => a - b);
    return arr.map(x => sorted.indexOf(x) + 1);
  }, []);

  const calculateSpearmanCorrelation = useCallback((x, y) => {
    const rankX = getRanks(x);
    const rankY = getRanks(y);
    return calculatePearsonCorrelation(rankX, rankY);
  }, [getRanks, calculatePearsonCorrelation]);

  const calculateKendallCorrelation = useCallback((x, y) => {
    const n = x.length;
    let concordant = 0;
    let discordant = 0;

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const xDiff = x[i] - x[j];
        const yDiff = y[i] - y[j];
        if (xDiff * yDiff > 0) concordant++;
        else if (xDiff * yDiff < 0) discordant++;
      }
    }

    return (concordant - discordant) / (n * (n - 1) / 2);
  }, []);

  const calculateCorrelation = useCallback((x, y, method) => {
    const n = x.length;
    if (n !== y.length) return 0;

    switch (method) {
      case 'pearson':
        return calculatePearsonCorrelation(x, y);
      case 'spearman':
        return calculateSpearmanCorrelation(x, y);
      case 'kendall':
        return calculateKendallCorrelation(x, y);
      default:
        return calculatePearsonCorrelation(x, y);
    }
  }, [calculatePearsonCorrelation, calculateSpearmanCorrelation, calculateKendallCorrelation]);

  // 添加计算相关性矩阵的函数
  const calculateCorrelationMatrix = useCallback(() => {
    if (!data.length || loading) return;

    // 1. 筛选数据
    const filteredData = data.filter(d => {
      return selectedMainGroups.includes(d.main_element_group) &&
        d.yield_strength >= propertyRanges.yield_strength[0] &&
        d.yield_strength <= propertyRanges.yield_strength[1] &&
        d.ultimate_strength >= propertyRanges.ultimate_strength[0] &&
        d.ultimate_strength <= propertyRanges.ultimate_strength[1] &&
        d.elongation >= propertyRanges.elongation[0] &&
        d.elongation <= propertyRanges.elongation[1];
    });

    // 2. 统计元素出现频率
    const newElementCounts = {};
    filteredData.forEach(d => {
      if (d.composition) {
        Object.keys(d.composition).forEach(el => {
          newElementCounts[el] = (newElementCounts[el] || 0) + 1;
        });
      }
    });
    setElementCounts(newElementCounts);

    // 3. 筛选满足频率要求的元素
    const validElements = Object.entries(newElementCounts)
      .filter(([, count]) => count >= elementFrequencyRange[0] && count <= elementFrequencyRange[1])
      .map(([el]) => el)
      .sort();

    // 4. 构建元素含量矩阵
    const elementMatrix = filteredData.map(d => {
      const row = {};
      validElements.forEach(el => {
        row[el] = d.composition?.[el] || 0;
      });
      return row;
    });

    // 5. 计算相关性矩阵
    const matrix = validElements.map(el1 => {
      return validElements.map(el2 => {
        const values1 = elementMatrix.map(row => row[el1]);
        const values2 = elementMatrix.map(row => row[el2]);
        return calculateCorrelation(values1, values2, correlationMethod);
      });
    });

    setCorrelationElements(validElements);
    setCorrelationMatrix(matrix);
  }, [data, loading, selectedMainGroups, propertyRanges, elementFrequencyRange, correlationMethod, calculateCorrelation]);

  // 修改 useEffect，添加适当的依赖和条件检查
  useEffect(() => {
    if (!loading && data.length > 0) {
      calculateCorrelationMatrix();
    }
  }, [data, loading, calculateCorrelationMatrix]);

  // 修改 correlationData useMemo，移除不必要的 axisOptions 依赖
  const correlationData = useMemo(() => {
    if (!correlationMatrix.length || !correlationElements.length) return null;
    
    return [{
      z: correlationMatrix,
      x: correlationElements,
      y: correlationElements,
      type: 'heatmap',
      colorscale: [
        [0, '#2166ac'],    // 深蓝
        [0.5, '#f7f7f7'],  // 白色
        [1, '#b2182b']     // 深红
      ],
      zmin: -1,
      zmax: 1,
      colorbar: {
        title: `${correlationMethod.charAt(0).toUpperCase() + correlationMethod.slice(1)} r`,
        titleside: 'right',
        thickness: 20,
        len: 0.8
      },
      hovertemplate: 
        '<b>Elements:</b> %{y} - %{x}<br>' +
        '<b>Correlation:</b> %{z:.3f}<br>' +
        '<extra></extra>'
    }];
  }, [correlationMatrix, correlationElements, correlationMethod]);

  // 在组件中使用 elementCountsData
  const elementCountsData = useMemo(() => {
    return Object.entries(elementCounts).map(([element, count]) => ({
      element,
      count,
      percentage: (count / data.length) * 100
    }));
  }, [elementCounts, data.length]);

  // 在组件中使用 renderCorrelationHeatmap
  const renderCorrelationHeatmap = () => {
    if (!correlationData) return null;
    return (
      <Plot
        data={correlationData}
        layout={{
          title: 'Element Correlation Matrix',
          width: 800,
          height: 600,
          margin: { t: 50, b: 50, l: 50, r: 50 }
        }}
      />
    );
  };

  // 在组件中使用 handlePropertyRangeChange
  // 添加用于触发计算的状态
  const [propertyCalculationTrigger, setPropertyCalculationTrigger] = useState(0);
  
  // 添加固定的滑块范围状态，避免实时变化
  const [fixedPropertyRanges, setFixedPropertyRanges] = useState({
    yield_strength: [0, 1000],
    ultimate_strength: [0, 1000], 
    elongation: [0, 100],
    density: [0, 10]
  });
  
  // 为Element-Property Correlation创建独立的elementCounts状态
  const [propertyElementCounts, setPropertyElementCounts] = useState({});
  const [fixedElementFrequencyMax, setFixedElementFrequencyMax] = useState(100);

  // 修改处理函数，使用 useCallback 和更短的 debounce 时间
  const handleRangeChange = debounce((property, newValue) => {
    setPropertyRanges(prev => ({
      ...prev,
      [property]: newValue
    }));
    // 触发计算
    setPropertyCalculationTrigger(prev => prev + 1);
  }, 100);

  // 添加即时更新函数
  const handleRangeChangeImmediate = useCallback((property, newValue) => {
    setPropertyRanges(prev => ({
      ...prev,
      [property]: newValue
    }));
  }, []);

  // 在组件中使用 setElementFrequencyRange 和 setCorrelationMethod
  const handleElementFrequencyChange = (newValue) => {
    setElementFrequencyRange(newValue);
    setPropertyCalculationTrigger(prev => prev + 1);
  };

  const handleCorrelationMethodChange = (newValue) => {
    setCorrelationMethod(newValue);
    setPropertyCalculationTrigger(prev => prev + 1);
  };

  // 修改计算元素-性能相关性的函数
  const calculatePropertyCorrelation = useCallback(() => {
    if (!data.length || loading) return;

    // 1. 筛选数据
    const filteredData = data.filter(d => {
      return selectedMainGroups.includes(d.main_element_group) &&
        d.yield_strength >= propertyRanges.yield_strength[0] &&
        d.yield_strength <= propertyRanges.yield_strength[1] &&
        d.ultimate_strength >= propertyRanges.ultimate_strength[0] &&
        d.ultimate_strength <= propertyRanges.ultimate_strength[1] &&
        d.elongation >= propertyRanges.elongation[0] &&
        d.elongation <= propertyRanges.elongation[1] &&
        d.density >= propertyRanges.density[0] &&
        d.density <= propertyRanges.density[1];
    });

    // 2. 统计元素出现频率
    const elementCounts = {};
    filteredData.forEach(d => {
      if (d.composition) {
        Object.keys(d.composition).forEach(el => {
          elementCounts[el] = (elementCounts[el] || 0) + 1;
        });
      }
    });
    
    // 更新Property Correlation专用的elementCounts
    setPropertyElementCounts(elementCounts);
    
    // 更新固定的元素频率最大值（只在计算完成后更新）
    const newMaxCount = Object.keys(elementCounts).length > 0 ? 
      Math.max(...Object.values(elementCounts)) : 100;
    setFixedElementFrequencyMax(newMaxCount);
    
    // 如果当前的频率范围超出了新的最大值，需要调整
    setElementFrequencyRange(prev => [
      prev[0], 
      Math.min(prev[1], newMaxCount)
    ]);

    // 3. 筛选满足频率要求的元素
    const validElements = Object.entries(elementCounts)
      .filter(([, count]) => count >= elementFrequencyRange[0] && count <= elementFrequencyRange[1])
      .map(([el]) => el)
      .sort();

    // 4. 计算每个元素与所有性能指标的相关性（根据分析模式）
    const properties = ['yield_strength', 'ultimate_strength', 'elongation', 'density'];
    const matrix = properties.map(property => {
      return validElements.map(element => {
        let elementValues;
        switch (propertyAnalysisMode) {
          case 'occurrence':
            // 二元关系：元素是否存在（0或1）
            elementValues = filteredData.map(d => d.composition?.[element] && d.composition[element] > 0 ? 1 : 0);
            break;
          case 'atomic':
            // 原子分数（保持原始值，因为数据库中存储的就是at%）
            elementValues = filteredData.map(d => d.composition?.[element] || 0);
            break;
          case 'weight':
          default:
            // 重量分数（需要转换）
            elementValues = filteredData.map(d => {
              const weightComposition = convertAtomicToWeight(d.composition);
              return weightComposition[element] || 0;
            });
            break;
        }
        const propertyValues = filteredData.map(d => d[property]);
        return calculateCorrelation(elementValues, propertyValues, correlationMethod);
      });
    });

    setPropertyCorrelationElements(validElements);
    setPropertyCorrelationMatrix(matrix);
  }, [data, loading, selectedMainGroups, propertyRanges, elementFrequencyRange, correlationMethod, propertyAnalysisMode, calculateCorrelation, convertAtomicToWeight]);

  // 添加获取性能指标范围的函数
  const getPropertyRange = useCallback((property) => {
    if (!data.length) return [0, 100];
    const filteredData = data.filter(d => selectedMainGroups.includes(d.main_element_group));
    if (!filteredData.length) return [0, 100];
    const values = filteredData
      .map(d => d[property])
      .filter(v => typeof v === 'number' && isFinite(v));
    if (!values.length) return [0, 100];
    
    if (property === 'density') {
      // 密度使用0.1的颗粒度
      return [
        Math.floor(Math.min(...values) * 10) / 10,
        Math.ceil(Math.max(...values) * 10) / 10
      ];
    } else {
      // 其他属性使用整数颗粒度
      return [
        Math.floor(Math.min(...values)),
        Math.ceil(Math.max(...values))
      ];
    }
  }, [data, selectedMainGroups]);

  // 修改 useEffect 来更新性能指标范围
  useEffect(() => {
    if (!loading && data.length > 0) {
      const yieldRange = getPropertyRange('yield_strength');
      const ultimateRange = getPropertyRange('ultimate_strength');
      const elongationRange = getPropertyRange('elongation');
      const densityRange = getPropertyRange('density');
      
      setPropertyRanges(prev => ({
        ...prev,
        yield_strength: yieldRange,
        ultimate_strength: ultimateRange,
        elongation: elongationRange,
        density: densityRange
      }));
      
      // 设置固定的滑块范围
      setFixedPropertyRanges({
        yield_strength: yieldRange,
        ultimate_strength: ultimateRange,
        elongation: elongationRange,
        density: densityRange
      });
      
      // 设置固定的元素频率最大值
      if (Object.keys(propertyElementCounts).length > 0) {
        const maxCount = Math.max(...Object.values(propertyElementCounts));
        setFixedElementFrequencyMax(maxCount);
      }
      
      // 触发计算
      setPropertyCalculationTrigger(prev => prev + 1);
    }
  }, [data, loading, selectedMainGroups, getPropertyRange, propertyElementCounts]);

  // 修改 useEffect，只在必要时更新矩阵
  useEffect(() => {
    if (!loading && data.length > 0) {
      const timer = setTimeout(() => {
        calculatePropertyCorrelation();
      }, 300); // 添加延迟，避免频繁更新
      return () => clearTimeout(timer);
    }
  }, [data, loading, selectedMainGroups, elementFrequencyRange, correlationMethod, propertyAnalysisMode, propertyCalculationTrigger, calculatePropertyCorrelation, propertyElementCounts]);

  // 删除getDefaultElementFrequencyRange函数，已移到useEffect内部

  // 只在初始加载时设置默认值，不在滑动时覆盖
  useEffect(() => {
    const getDefaultRange = (counts) => {
      if (Object.keys(counts).length === 0) return [0, 1];
      const maxCount = Math.max(...Object.values(counts));
      return [0, Math.max(1, Math.ceil(maxCount * 0.3))];
    };

    if (!elementFrequencyRangeInitialized && Object.keys(propertyElementCounts).length > 0) {
      const maxCount = Math.max(...Object.values(propertyElementCounts));
      setElementFrequencyRange(getDefaultRange(propertyElementCounts));
      setFixedElementFrequencyMax(maxCount);
      setElementFrequencyRangeInitialized(true);
    }
  }, [propertyElementCounts, elementFrequencyRangeInitialized]); // 已经包含了propertyElementCounts依赖

  // 添加样式常量
  const commonStyles = {
    backgroundColor: '#f8f9fa',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #eaeaea',
    marginBottom: '24px'
  };


  const sourceContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1
  };

  const sourceHeaderStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px'
  };

  const sourceLabelStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333'
  };

  const sourceGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '8px',
    maxHeight: '120px',
    overflowY: 'auto',
    padding: '8px',
    backgroundColor: 'white',
    borderRadius: '4px',
    border: '1px solid #ddd'
  };

  const pieToolbarStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const pieButtonStyles = (active) => ({
    padding: '8px 12px',
    borderRadius: '6px',
    border: active ? '1px solid #52af77' : '1px solid #d0d5dd',
    backgroundColor: active ? '#e9f6ee' : '#fff',
    color: active ? '#2d7a50' : '#344054',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'all 0.15s ease'
  });

  const uploadPillStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d0d5dd',
    backgroundColor: '#fff'
  };

  const hasUserUpload = userUploadData.length > 0;
  const hasMainGroupPieData = (pieData.mainGroupPieData?.[0]?.values || []).length > 0;
  const hasElementPieData = pieData.elementPieData.length > 0;
  const dataSourceControls = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
      <div style={pieToolbarStyles}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', color: '#333', fontWeight: 600 }}>Data Source:</span>
          {[
            { value: PIE_DISPLAY_MODES.USER, label: 'User data only', requiresUpload: true },
            { value: PIE_DISPLAY_MODES.DEFAULT, label: 'Original data only' },
            { value: PIE_DISPLAY_MODES.COMBINED, label: 'Combined view', requiresUpload: true }
          ].map((option) => (
            <button
              key={option.value}
              style={{
                ...pieButtonStyles(dataDisplaySource === option.value),
                opacity: option.requiresUpload && !hasUserUpload ? 0.6 : 1,
                cursor: option.requiresUpload && !hasUserUpload ? 'not-allowed' : 'pointer'
              }}
              disabled={option.requiresUpload && !hasUserUpload}
              onClick={() => setDataDisplaySource(option.value)}
            >
              {option.label}
            </button>
          ))}
          {!hasUserUpload && (
            <span style={{ fontSize: '12px', color: '#667085' }}>Upload a CSV to enable user data view.</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <input
            id="main-group-upload"
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleDataCsvUpload(file);
                e.target.value = '';
              }
            }}
          />
          <label htmlFor="main-group-upload" style={{ ...uploadPillStyles, cursor: 'pointer' }}>
            <span role="img" aria-label="upload">📤</span>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Upload CSV</span>
          </label>
          <span style={{ fontSize: '13px', color: '#475467' }}>
            {dataUploadName || 'No file uploaded'}
          </span>
        </div>
      </div>
      {dataUploadError ? (
        <div style={{ color: '#d14343', fontSize: '13px' }}>{dataUploadError}</div>
      ) : null}
      <div style={{ fontSize: '12px', color: '#667085' }}>
        We do not store or retain your data. Uploaded CSVs are processed for analysis and discarded.
      </div>
      <button
        type="button"
        onClick={() => setShowDataFormat(prev => !prev)}
        style={{
          alignSelf: 'flex-start',
          background: 'none',
          border: 'none',
          padding: 0,
          color: '#2d7a50',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: 600
        }}
      >
        {showDataFormat ? 'Hide CSV requirements' : 'View CSV requirements'}
      </button>
      {showDataFormat ? (
        <div style={{ fontSize: '12px', color: '#667085', lineHeight: 1.6 }}>
          Required: alloy composition via `composition` (JSON) or element columns (e.g., Fe, Ni), plus at least one property
          column (`yield_strength`, `ultimate_strength`, `elongation`, `density`, `avg_strength`).
          Optional: `main_elem` or `main_element_group` (auto-derived if missing), `process_condition`, `test_condition`, `source`,
          `ultimate_strength_compression`, `bm`, `em`.
        </div>
      ) : null}
    </div>
  );

  return (
    <div style={{ width: '100%', padding: '24px 24px 100px 24px' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          aria-label="data visualization tabs"
          sx={{
            '& .MuiTab-root': {
              fontSize: '16px',
              textTransform: 'none',
              minWidth: 120,
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: '#52af77 !important',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#52af77',
            },
          }}
        >
          <Tab label="Overview" />
          <Tab label="Scatter Plot" />
          <Tab label="Element Analysis" />
          <Tab label="Element Correlation" />
          <Tab label="Element-Property Correlation" />
          <Tab label="Element Co-occurrence" />
          <Tab label="Feature Analysis" />
          <Tab label="Simulation Data" />
        </Tabs>
      </Box>

      {/* 对于Simulation Data选项卡，不显示数据源选择器 */}
      {tabValue !== 7 && (
        <>
      {tabValue === 0 ? (
        <div style={commonStyles}>
          <div style={sourceContainerStyles}>
            <div style={sourceHeaderStyles}>
              <label style={sourceLabelStyles}>Main Element Groups:</label>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedMainGroups.length === availableMainGroups.length}
                    indeterminate={selectedMainGroups.length > 0 && selectedMainGroups.length < availableMainGroups.length}
                    onChange={handleSelectAllGroups}
                    sx={{
                      color: '#52af77',
                      '&.Mui-checked': {
                        color: '#52af77',
                      },
                    }}
                  />
                }
                label="Select All"
                sx={{ margin: 0 }}
              />
            </div>
            <div style={sourceGridStyles}>
              {availableMainGroups.map(group => (
                <FormControlLabel
                  key={group}
                  control={
                    <Checkbox
                      checked={selectedMainGroups.includes(group)}
                      onChange={() => handleMainGroupChange(group)}
                      sx={{
                        color: '#52af77',
                        '&.Mui-checked': {
                          color: '#52af77',
                        },
                      }}
                    />
                  }
                  label={group}
                  sx={{ margin: 0 }}
                />
              ))}
            </div>
            {dataSourceControls}
          </div>
        </div>
      ) : (
        <div style={{ ...commonStyles, marginBottom: 8 }}>
          <div style={sourceContainerStyles}>
            <div style={sourceHeaderStyles}>
              <label style={sourceLabelStyles}>Main Element Groups:</label>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedMainGroups.length === availableMainGroups.length}
                    indeterminate={selectedMainGroups.length > 0 && selectedMainGroups.length < availableMainGroups.length}
                    onChange={handleSelectAllGroups}
                    sx={{
                      color: '#52af77',
                      '&.Mui-checked': {
                        color: '#52af77',
                      },
                    }}
                  />
                }
                label="Select All"
                sx={{ margin: 0 }}
              />
            </div>
            <div style={sourceGridStyles}>
              {availableMainGroups.map(group => (
                <FormControlLabel
                  key={group}
                  control={
                    <Checkbox
                      checked={selectedMainGroups.includes(group)}
                      onChange={() => handleMainGroupChange(group)}
                      sx={{
                        color: '#52af77',
                        '&.Mui-checked': {
                          color: '#52af77',
                        },
                      }}
                    />
                  }
                  label={group}
                  sx={{ margin: 0 }}
                />
              ))}
            </div>
            {dataSourceControls}
          </div>
        </div>
          )}
        </>
      )}

      <TabPanel value={tabValue} index={0}>
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '16px',
          padding: '0 20px'
        }}>
          {/* 饼图展示区域 */}
          <div style={{ 
            display: 'flex', 
            gap: '24px', 
            marginTop: '20px',
            minHeight: '600px'  // 设置最小高度
          }}>
            {/* 左侧饼图 */}
            <div style={{
              flex: 1,
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '600px'  // 设置最小高度
            }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                marginBottom: '16px',
                color: '#333'
              }}>
                Main Element Group Distribution
              </div>
              {!loading && hasMainGroupPieData ? (
                <div style={{ 
                  flex: 1, 
                  width: '100%',
                  position: 'relative'  // 添加相对定位
                }}>
                  <Plot
                    data={pieData.mainGroupPieData}
                    layout={{
                      ...pieLayout,
                      title: {
                        text: 'Distribution by Main Element Group',
                        font: { size: 16 }
                      }
                    }}
                    useResizeHandler
                    style={{ 
                      width: '100%', 
                      height: '100%',
                      position: 'absolute',  // 使用绝对定位
                      top: 0,
                      left: 0
                    }}
                    config={{ responsive: true }}  // 添加响应式配置
                    hovertemplate={pieHoverTemplate}
                  />
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  color: '#666',
                  fontSize: '16px'
                }}>
                  {dataDisplaySource === PIE_DISPLAY_MODES.USER && !hasUserUpload
                    ? 'Upload a CSV to view user Main Element Group distribution'
                    : 'No main element distribution data for current selection'}
                </div>
              )}
            </div>

            {/* 右侧饼图 */}
            <div style={{
              flex: 1,
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '600px'  // 设置最小高度
            }}>
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Element Distribution
                </div>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center'
                }}>
                  <label style={{ fontSize: '14px' }}>Display Mode:</label>
                  <select
                    value={elementDisplayMode}
                    onChange={(e) => setElementDisplayMode(e.target.value)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="count">Occurrence Count</option>
                  </select>
                  {elementDisplayMode === 'percentage' && (
                    <>
                      <label style={{ fontSize: '14px' }}>Unit:</label>
                      <select
                        value={elementDistributionUnit}
                        onChange={(e) => setElementDistributionUnit(e.target.value)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="at">Atomic % (at%)</option>
                        <option value="wt">Weight % (wt%)</option>
                      </select>
                    </>
                  )}
                </div>
              </div>
              {!loading && hasElementPieData ? (
                <div style={{ 
                  flex: 1, 
                  width: '100%',
                  position: 'relative'  // 添加相对定位
                }}>
                  <Plot
                    data={pieData.elementPieData}
                    layout={{
                      ...pieLayout,
                      title: {
                        text: elementDisplayMode === 'percentage' 
                          ? `Distribution by Element ${elementDistributionUnit === 'wt' ? 'Weight' : 'Atomic'} Percentage`
                          : 'Distribution by Element Occurrence',
                        font: { size: 16 }
                      }
                    }}
                    useResizeHandler
                    style={{ 
                      width: '100%', 
                      height: '100%',
                      position: 'absolute',  // 使用绝对定位
                      top: 0,
                      left: 0
                    }}
                    config={{ responsive: true }}  // 添加响应式配置
                    hovertemplate={pieHoverTemplate}
                  />
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  color: '#666',
                  fontSize: '16px'
                }}>
                  {dataDisplaySource === PIE_DISPLAY_MODES.USER && !hasUserUpload
                    ? 'Upload a CSV to view user element distribution'
                    : 'No element distribution data for the current selection'}
                </div>
              )}
            </div>
          </div>

          {/* 数据漏斗（Data Overview） */}
          <section className="section data-overview-section data-pipeline-section" style={{ padding: 0 }}>
            <div className="section-content" style={{ padding: 0 }}>
              <h2 className="section-title" style={{ paddingLeft: 20 }}>Data Overview</h2>
              <p className="section-intro" style={{ paddingLeft: 20 }}>
                Our comprehensive data processing pipeline transforms raw materials data into high-quality training datasets through systematic filtering and validation:
              </p>

              <div className="data-funnel" style={{ marginTop: 0 }}>
                {/* Top Layer - Main Element Groups */}
                <div className="funnel-layer funnel-top">
                  <div className="funnel-shape funnel-shape-top">
                    <div className="funnel-content">
                      <div className="funnel-left">
                        <div className="layer-title">Main Element Buckets</div>
                        <div className="data-sources">
                          <div className="source-item">
                            <div className="source-icon">Mg</div>
                            <span>Mg</span>
                          </div>
                          <div className="source-item">
                            <div className="source-icon">M</div>
                            <span>Multi</span>
                          </div>
                          <div className="source-item">
                            <div className="source-icon">Fe</div>
                            <span>Fe</span>
                          </div>
                          <div className="source-item">
                            <div className="source-icon">Cu</div>
                            <span>Cu</span>
                          </div>
                          <div className="source-item">
                            <div className="source-icon">Al</div>
                            <span>Al</span>
                          </div>
                          <div className="source-item">
                            <div className="source-icon">⋯</div>
                            <span>Other (Minor)</span>
                          </div>
                        </div>
                      </div>
                      <div className="funnel-right">
                        <div className="data-count">
                          <span className="count-number">15,078</span>
                          <span className="count-label">Raw Entries</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* First Funnel Connector */}
                <div className="funnel-connector first-connector">
                  <div className="connector-shape"></div>
                </div>

                {/* Middle Layer - Preprocessing */}
                <div className="funnel-layer funnel-middle">
                  <div className="funnel-shape funnel-shape-middle">
                    <div className="funnel-content">
                      <div className="funnel-left">
                        <div className="layer-title">Data Preprocessing</div>
                        <div className="processing-steps">
                          <div className="step-item">
                            <div className="step-icon">🧹</div>
                            <span>Data Cleaning</span>
                          </div>
                          <div className="step-item">
                            <div className="step-icon">🔍</div>
                            <span>Missing Values</span>
                          </div>
                          <div className="step-item">
                            <div className="step-icon">📊</div>
                            <span>Standardization</span>
                          </div>
                        </div>
                      </div>
                      <div className="funnel-right">
                        <div className="data-count">
                          <span className="count-number">6,614</span>
                          <span className="count-label">Cleaned Entries</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Second Funnel Connector */}
                <div className="funnel-connector second-connector">
                  <div className="connector-shape"></div>
                </div>

                {/* Bottom Layer - Final Dataset */}
                <div className="funnel-layer funnel-bottom">
                  <div className="funnel-shape funnel-shape-bottom">
                    <div className="funnel-content">
                      <div className="funnel-left">
                        <div className="layer-title">Quality Validation</div>
                        <div className="validation-criteria">
                          <div className="criteria-item">
                            <div className="criteria-icon">⚙️</div>
                            <span>Processing</span>
                          </div>
                          <div className="criteria-item">
                            <div className="criteria-icon">🧪</div>
                            <span>Testing</span>
                          </div>
                        </div>
                      </div>
                      <div className="funnel-right">
                        <div className="data-count">
                          <span className="count-number">2,931</span>
                          <span className="count-label">Training Set</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Support Arrow to Model */}
                <div className="support-arrow">
                  <div className="support-shape">
                    <div className="support-icon">⬇</div>
                    <div className="support-text">Powers</div>
                  </div>
                </div>

                {/* Model Training */}
                <div className="model-training">
                  <div className="training-box">
                    <div className="training-icon">🧠</div>
                    <div className="training-text">
                      <div className="training-title">AI Model Training</div>
                      <div className="training-subtitle">Data-Driven Discovery</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 元素分布分析图 */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px 32px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #eaeaea',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#333'
              }}>
                Element Distribution Analysis
              </div>
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center'
              }}>
                <label style={{ fontSize: '16px', color: '#444' }}>Select Element:</label>
                <select
                  value={selectedElement}
                  onChange={e => setSelectedElement(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                    backgroundColor: 'white',
                    color: '#444'
                  }}
                >
                  {allElements.map(el => (
                    <option key={el} value={el}>{el}</option>
                  ))}
                </select>
                <label style={{ fontSize: '16px', color: '#444' }}>Unit:</label>
                <select
                  value={elementAnalysisUnit}
                  onChange={e => setElementAnalysisUnit(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                    backgroundColor: 'white',
                    color: '#444'
                  }}
                >
                  <option value="at">Atomic % (at%)</option>
                  <option value="wt">Weight % (wt%)</option>
                </select>
              </div>
            </div>
            {!loading && calculateElementDistributionData().length > 0 ? (
              <div style={{ width: '100%', height: '600px', position: 'relative' }}>
                <Plot
                  data={calculateElementDistributionData()}
                  layout={elementDistributionLayout}
                  useResizeHandler
                  style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
                  config={{ responsive: true }}
                />
              </div>
            ) : (
              <div style={{ color: '#666', fontSize: '16px', padding: '32px 0', textAlign: 'center' }}>
                No distribution data available for the selected element
              </div>
            )}
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#444'
            }}>
              <div style={{ fontWeight: '500', marginBottom: '8px' }}>Description:</div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>This chart shows the {elementAnalysisUnit === 'wt' ? 'weight' : 'atomic'} fraction distribution of the selected element in all alloys containing it.</li>
                <li>The histogram is normalized to probability density, and the red curve is the Kernel Density Estimation (KDE).</li>
                <li>You can select different elements for analysis and switch between atomic % (at%) and weight % (wt%) using the dropdown menus.</li>
                <li>Only alloys with a {elementAnalysisUnit === 'wt' ? 'weight' : 'atomic'} fraction greater than 0 for the selected element are included in the distribution.</li>
              </ul>
            </div>
          </div>

          {/* 属性分布分析图 */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px 32px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #eaeaea',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#333'
              }}>
                Property Distribution Analysis
              </div>
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center'
              }}>
                <label style={{ fontSize: '16px', color: '#444' }}>Select Property:</label>
                <select
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                    backgroundColor: 'white',
                    color: '#444'
                  }}
                >
                  {propertyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {!loading && (
              <div style={{ width: '100%', height: '600px', position: 'relative' }}>
                <Plot
                  data={calculateDistributionData()}
                  layout={distributionLayout}
                  useResizeHandler
                  style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
                  config={{ responsive: true }}
                />
              </div>
            )}
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#444'
            }}>
              <div style={{ fontWeight: '500', marginBottom: '8px' }}>Description:</div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>This chart shows the distribution of the selected property (such as density, strength, etc.) in all selected main element groups.</li>
                <li>The histogram is normalized to probability density, and the red curve is the Kernel Density Estimation (KDE).</li>
                <li>You can select different properties for analysis using the dropdown menu.</li>
                <li>Only samples with valid values for the selected property are included in the distribution.</li>
              </ul>
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '12px',
          padding: '0 20px'
        }}>
          {/* 坐标轴选择行 */}
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
              <label style={{ minWidth: '70px', fontSize: '14px', fontWeight: '500' }}>X Axis:</label>
              <select 
                value={axes.x}
                onChange={(e) => handleAxisChange('x', e.target.value)}
                style={{
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  flex: 1
                }}
              >
                {axisXYOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
              <label style={{ minWidth: '70px', fontSize: '14px', fontWeight: '500' }}>Y Axis:</label>
              <select 
                value={axes.y}
                onChange={(e) => handleAxisChange('y', e.target.value)}
                style={{
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  flex: 1
                }}
              >
                {axisXYOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
              <label style={{ minWidth: '70px', fontSize: '14px', fontWeight: '500' }}>Point Size:</label>
              <select 
                value={axes.size}
                onChange={(e) => handleAxisChange('size', e.target.value)}
                style={{
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  flex: 1
                }}
              >
                {axisOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 范围选择行 */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <label style={{ minWidth: '72px', fontSize: '14px', fontWeight: '500' }}>X Range:</label>
              <PrettoSlider
                value={getAxisValue(axes.x)}
                onChange={(_, newValue) => handleAxisRangeChange(axes.x, newValue)}
                valueLabelDisplay="auto"
                min={getAxisRange(axes.x)[0]}
                max={getAxisRange(axes.x)[1]}
                style={{ flex: 1, margin: '0 6px' }}
              />
              <span style={{ minWidth: '105px', fontSize: '13px', color: '#666', textAlign: 'right' }}>
                {getAxisValue(axes.x)[0]} - {getAxisValue(axes.x)[1]} {getAxisUnit(axes.x)}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <label style={{ minWidth: '72px', fontSize: '14px', fontWeight: '500' }}>Y Range:</label>
              <PrettoSlider
                value={getAxisValue(axes.y)}
                onChange={(_, newValue) => handleAxisRangeChange(axes.y, newValue)}
                valueLabelDisplay="auto"
                min={getAxisRange(axes.y)[0]}
                max={getAxisRange(axes.y)[1]}
                style={{ flex: 1, margin: '0 6px' }}
              />
              <span style={{ minWidth: '105px', fontSize: '13px', color: '#666', textAlign: 'right' }}>
                {getAxisValue(axes.y)[0]} - {getAxisValue(axes.y)[1]} {getAxisUnit(axes.y)}
              </span>
            </div>

            {axes.size !== 'fixed' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                <label style={{ minWidth: '82px', fontSize: '14px', fontWeight: '500' }}>Size Range:</label>
                <PrettoSlider
                  value={getAxisValue(axes.size)}
                  onChange={(_, newValue) => handleAxisRangeChange(axes.size, newValue)}
                  valueLabelDisplay="auto"
                  min={getAxisRange(axes.size)[0]}
                  max={getAxisRange(axes.size)[1]}
                  style={{ flex: 1, margin: '0 6px' }}
                />
                <span style={{ minWidth: '105px', fontSize: '13px', color: '#666', textAlign: 'right' }}>
                  {getAxisValue(axes.size)[0]} - {getAxisValue(axes.size)[1]} {getAxisUnit(axes.size)}
                </span>
              </div>
            )}
          </div>
        </div>

        {loading ? <div>Loading...</div> : (
          <>
            <Plot 
              data={plotData} 
              layout={layout} 
              useResizeHandler 
              style={{ 
                width: '100%', 
                height: 700,
                border: '1px solid #E5E5E5',
                borderRadius: '4px',
                padding: '10px',
                marginBottom: '40px'
              }} 
            />
            <div style={{
              marginTop: '24px',
              padding: '20px 32px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#444',
              marginBottom: '60px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              border: '1px solid #eaeaea'
            }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                marginBottom: '12px',
                color: '#333'
              }}>
                Chart Description
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '24px' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: '500' }}>Main Element Group Selection: </span>
                    Use the checkboxes to focus on one or multiple main element groups.
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: '500' }}>Axis Selection: </span>
                    You can select any property as X and Y axes, including density, yield strength, etc.
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: '500' }}>Point Size Control: </span>
                    You can choose any property or fixed size to control the data point size.
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '24px' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: '500' }}>Color Differentiation: </span>
                    Data points are colored by main element classification. If no element exceeds 50% of the composition, it is labeled as "Multi".
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: '500' }}>Range Control: </span>
                    Each axis has a corresponding range slider to adjust the displayed data range.
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: '500' }}>Point Size Range: </span>
                    Point size range control can be adjusted independently of the coordinate axes.
                  </div>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontWeight: '500', marginBottom: '8px' }}>Data Properties:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div><span style={{ fontWeight: '500' }}>• Density: </span>Material density in g/cm³</div>
                    <div><span style={{ fontWeight: '500' }}>• Average Strength: </span>Mean of yield and ultimate strength in MPa</div>
                    <div><span style={{ fontWeight: '500' }}>• Elongation: </span>Percentage of elongation before fracture</div>
                    <div><span style={{ fontWeight: '500' }}>• Yield Strength: </span>Stress at which material begins to deform plastically in MPa</div>
                    <div><span style={{ fontWeight: '500' }}>• Ultimate Strength: </span>Maximum stress the material can withstand in MPa</div>
                    <div><span style={{ fontWeight: '500' }}>• Ultimate Strength/Density: </span>Specific strength ratio (strength-to-weight ratio)</div>
                    <div><span style={{ fontWeight: '500' }}>• Yield Strength/Density: </span>Specific yield strength ratio</div>
                    <div><span style={{ fontWeight: '500' }}>• Yield Strength × Elongation: </span>Toughness indicator combining strength and ductility</div>
                    <div><span style={{ fontWeight: '500' }}>• Ultimate Strength × Elongation: </span>Ultimate toughness indicator</div>
                    <div><span style={{ fontWeight: '500' }}>• Chemical Composition: </span>Available after publication</div>
                    <div><span style={{ fontWeight: '500' }}>• Process Condition: </span>Material preparation process parameters</div>
                    <div><span style={{ fontWeight: '500' }}>• Test Condition: </span>Material performance testing environment and parameters</div>
                    <div><span style={{ fontWeight: '500' }}>• Source: </span>Reference for experimental data</div>
                  </div>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontWeight: '500', marginBottom: '8px' }}>Interactive Features:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div>• Hover over data points to view detailed information</div>
                    <div>• Click legend items to show/hide data for specific elements</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '16px',
          padding: '0 20px'
        }}>
          {/* 控制面板 */}
          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #eaeaea'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: '500' }}>Select Element:</label>
              <select 
                value={elementAnalysisElement}
                onChange={(e) => setElementAnalysisElement(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  flex: 1
                }}
              >
                {allElements.map(element => (
                  <option key={element} value={element}>{element}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: '500' }}>Y-Axis Property:</label>
              <select 
                value={elementAnalysisProperty}
                onChange={(e) => setElementAnalysisProperty(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  flex: 1
                }}
              >
                {elementAnalysisPropertyOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: '500' }}>Composition Unit:</label>
              <select 
                value={elementAnalysisUnit}
                onChange={(e) => setElementAnalysisUnit(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  flex: 1
                }}
              >
                <option value="wt">Weight % (wt%)</option>
                <option value="at">Atomic % (at%)</option>
              </select>
            </div>
          </div>

          {/* 散点图 */}
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '600px',
              fontSize: '18px',
              color: '#666'
            }}>
              Loading...
            </div>
          ) : (
            <>
              <Plot 
                data={calculateElementAnalysisData()} 
                layout={elementAnalysisLayout} 
                useResizeHandler 
                style={{ 
                  width: '100%', 
                  height: 600,
                  border: '1px solid #E5E5E5',
                  borderRadius: '4px',
                  padding: '10px',
                  marginBottom: '40px'
                }} 
              />
              
              {/* 说明文档 */}
              <div style={{
                marginTop: '24px',
                padding: '20px 32px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '16px',
                lineHeight: '1.8',
                color: '#444',
                marginBottom: '60px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                border: '1px solid #eaeaea'
              }}>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: '600', 
                  marginBottom: '12px',
                  color: '#333'
                }}>
                  Element Analysis Description
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <span style={{ fontWeight: '500' }}>Purpose: </span>
                    This analysis shows how the content of a specific element affects material properties. Each point represents an alloy containing the selected element.
                  </div>
                                                        <div>
                     <span style={{ fontWeight: '500' }}>X-Axis (Element Content): </span>
                     Shows the percentage of the selected element in the chosen unit. You can switch between Weight % (wt%) and Atomic % (at%) using the "Composition Unit" selector.
                   </div>
                   <div>
                     <span style={{ fontWeight: '500' }}>Y-Axis (Material Property): </span>
                     You can select from various material properties including basic properties (yield strength, ultimate strength, elongation, density) and derived properties (strength/density ratios, toughness indicators).
                   </div>
                   <div>
                     <span style={{ fontWeight: '500' }}>Composition Unit Selection: </span>
                     Choose between Atomic % (at%) or Weight % (wt%) for the X-axis display.
                   </div>
                   <div>
                    <span style={{ fontWeight: '500' }}>Data Filtering: </span>
                    Only alloys that contain the selected element (content &gt; 0%) from the selected main element groups are displayed.
                   </div>
                   <div>
                     <span style={{ fontWeight: '500' }}>Interactive Features: </span>
                     Hover over data points to see comprehensive information.
                   </div>
                  <div>
                    <span style={{ fontWeight: '500' }}>Applications: </span>
                    Use this analysis to understand element-property relationships, optimize alloy compositions, and identify trends in how specific elements influence material performance.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            fontSize: '18px',
            color: '#666',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #eaeaea'
          }}>
            Loading Element Correlation Analysis...
          </div>
        }>
          <ElementCorrelationTab 
            data={data}
            loading={loading}
            selectedMainGroups={selectedMainGroups}
            elementCountsData={elementCountsData}
            renderCorrelationHeatmap={renderCorrelationHeatmap}
            handleRangeChange={handleRangeChange}
            handleElementFrequencyChange={handleElementFrequencyChange}
            handleCorrelationMethodChange={handleCorrelationMethodChange}
          />
        </Suspense>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '16px',
          padding: '0 20px'
        }}>
          {/* 数据筛选控制面板 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: '24px',
            backgroundColor: '#f8f9fa',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #eaeaea'
          }}>
            {/* 第一行：Element Frequency Range、Correlation Method、Reset Ranges 横向排列，标签和范围在一行，滑块在下方 */}
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', marginBottom: 0 }}>
              {/* Element Frequency Range */}
              <div style={{ flex: 2, display: 'flex', flexDirection: 'column', alignItems: 'stretch', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Element Frequency Range:</span>
                  <span style={{ fontSize: '14px' }}>{elementFrequencyRange[0]} - {elementFrequencyRange[1]} occurrences</span>
                </div>
                <PrettoSlider
                  value={elementFrequencyRange}
                  onChange={(_, newValue) => handleElementFrequencyChange(newValue)}
                  valueLabelDisplay="auto"
                  min={0}
                  max={fixedElementFrequencyMax}
                  style={{ flex: 1 }}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 30, label: '30' },
                    { value: fixedElementFrequencyMax, label: 'Max' }
                  ]}
                />
              </div>
              {/* Correlation Method */}
              <div style={{ flex: 0.8, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: 8 }}>Correlation Method:</span>
                <select
                  value={correlationMethod}
                  onChange={(e) => handleCorrelationMethodChange(e.target.value)}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  <option value="pearson">Pearson</option>
                  <option value="spearman">Spearman</option>
                  <option value="kendall">Kendall</option>
                </select>
              </div>
              {/* Analysis Mode */}
              <div style={{ flex: 0.8, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: 8 }}>Analysis Mode:</span>
                <select
                  value={propertyAnalysisMode}
                  onChange={(e) => {
                    setPropertyAnalysisMode(e.target.value);
                    setPropertyCalculationTrigger(prev => prev + 1);
                  }}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  <option value="occurrence">Occurrence (0/1)</option>
                  <option value="atomic">Atomic Fraction (at%)</option>
                  <option value="weight">Weight Fraction (wt%)</option>
                </select>
              </div>
              {/* Reset Ranges */}
              <div style={{ flex: 0.8, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: 8 }}>Reset Ranges:</span>
                <button
                  onClick={() => {
                    const filteredData = data.filter(d => selectedMainGroups.includes(d.main_element_group));
                    if (filteredData.length > 0) {
                      // 先重置分析模式和相关性方法
                      setPropertyAnalysisMode('weight');
                      setCorrelationMethod('pearson');
                      
                      // 重置property ranges
                      const yieldRange = getPropertyRange('yield_strength');
                      const ultimateRange = getPropertyRange('ultimate_strength');
                      const elongationRange = getPropertyRange('elongation');
                      const densityRange = getPropertyRange('density');
                      
                      const newPropertyRanges = {
                        yield_strength: yieldRange,
                        ultimate_strength: ultimateRange,
                        elongation: elongationRange,
                        density: densityRange
                      };
                      
                      setPropertyRanges(newPropertyRanges);
                      
                      // 更新固定的滑块范围
                      setFixedPropertyRanges(newPropertyRanges);
                      
                      // 基于新的property ranges重新计算elementCounts
                      const resetFilteredData = filteredData.filter(d => {
                        return d.yield_strength >= newPropertyRanges.yield_strength[0] &&
                          d.yield_strength <= newPropertyRanges.yield_strength[1] &&
                          d.ultimate_strength >= newPropertyRanges.ultimate_strength[0] &&
                          d.ultimate_strength <= newPropertyRanges.ultimate_strength[1] &&
                          d.elongation >= newPropertyRanges.elongation[0] &&
                          d.elongation <= newPropertyRanges.elongation[1] &&
                          d.density >= newPropertyRanges.density[0] &&
                          d.density <= newPropertyRanges.density[1];
                      });
                      
                      const newElementCounts = {};
                      resetFilteredData.forEach(d => {
                        if (d.composition) {
                          Object.keys(d.composition).forEach(el => {
                            newElementCounts[el] = (newElementCounts[el] || 0) + 1;
                          });
                        }
                      });
                      
                      // 重置元素频率范围为30到最大值
                      const maxCount = Object.keys(newElementCounts).length > 0 ? 
                        Math.max(...Object.values(newElementCounts)) : 100;
                      setElementFrequencyRange([Math.min(30, maxCount), maxCount]);
                      
                      // 更新固定的元素频率最大值
                      setFixedElementFrequencyMax(maxCount);
                      
                      // 更新propertyElementCounts
                      setPropertyElementCounts(newElementCounts);
                      
                      // 触发计算
                      setPropertyCalculationTrigger(prev => prev + 1);
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: '1px solid #52af77',
                    backgroundColor: '#52af77',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  Reset All Ranges
                </button>
              </div>
            </div>
            {/* 第二行：四个性能指标滑块，每项标签和范围在一行，滑块在下方 */}
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', marginTop: 0 }}>
              {/* Yield Strength */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Yield Strength:</span>
                  <span style={{ fontSize: '14px' }}>{propertyRanges.yield_strength[0]} - {propertyRanges.yield_strength[1]} MPa</span>
                </div>
                            <PrettoSlider
              value={propertyRanges.yield_strength}
              onChange={(_, newValue) => handleRangeChangeImmediate('yield_strength', newValue)}
              onChangeCommitted={(_, newValue) => handleRangeChange('yield_strength', newValue)}
              valueLabelDisplay="auto"
              min={fixedPropertyRanges.yield_strength[0]}
              max={fixedPropertyRanges.yield_strength[1]}
              style={{ flex: 1 }}
            />
              </div>
              {/* Ultimate Strength */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Ultimate Strength:</span>
                  <span style={{ fontSize: '14px' }}>{propertyRanges.ultimate_strength[0]} - {propertyRanges.ultimate_strength[1]} MPa</span>
                </div>
                <PrettoSlider
                  value={propertyRanges.ultimate_strength}
                  onChange={(_, newValue) => handleRangeChangeImmediate('ultimate_strength', newValue)}
                  onChangeCommitted={(_, newValue) => handleRangeChange('ultimate_strength', newValue)}
                  valueLabelDisplay="auto"
                  min={fixedPropertyRanges.ultimate_strength[0]}
                  max={fixedPropertyRanges.ultimate_strength[1]}
                  style={{ flex: 1 }}
                />
              </div>
              {/* Elongation */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Elongation:</span>
                  <span style={{ fontSize: '14px' }}>{propertyRanges.elongation[0]} - {propertyRanges.elongation[1]} %</span>
                </div>
                <PrettoSlider
                  value={propertyRanges.elongation}
                  onChange={(_, newValue) => handleRangeChangeImmediate('elongation', newValue)}
                  onChangeCommitted={(_, newValue) => handleRangeChange('elongation', newValue)}
                  valueLabelDisplay="auto"
                  min={fixedPropertyRanges.elongation[0]}
                  max={fixedPropertyRanges.elongation[1]}
                  style={{ flex: 1 }}
                />
              </div>
              {/* Density */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Density:</span>
                  <span style={{ fontSize: '14px' }}>{propertyRanges.density[0].toFixed(1)} - {propertyRanges.density[1].toFixed(1)} g/cm³</span>
                </div>
                <PrettoSlider
                  value={propertyRanges.density}
                  onChange={(_, newValue) => handleRangeChangeImmediate('density', newValue)}
                  onChangeCommitted={(_, newValue) => handleRangeChange('density', newValue)}
                  valueLabelDisplay="auto"
                  min={fixedPropertyRanges.density[0]}
                  max={fixedPropertyRanges.density[1]}
                  step={0.1}
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          </div>

          {/* 相关性热力图 */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            width: '100%'  // 添加宽度100%
          }}>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              marginBottom: '16px',
              color: '#333'
            }}>
              Element-Property Correlation Analysis
            </div>
            {!loading && propertyCorrelationMatrix.length > 0 ? (
              <div style={{ width: '100%', height: '400px', position: 'relative' }}>
                <Plot
                  data={[{
                    z: propertyCorrelationMatrix,
                    x: propertyCorrelationElements,
                    y: ['YS', 'UTS', 'EL', 'DS'],
                    type: 'heatmap',
                    colorscale: [
                      [0, '#2166ac'],    // 深蓝
                      [0.5, '#f7f7f7'],  // 白色
                      [1, '#b2182b']     // 深红
                    ],
                    zmin: -1,
                    zmax: 1,
                    colorbar: {
                      title: `${correlationMethod.charAt(0).toUpperCase() + correlationMethod.slice(1)} r`,
                      titleside: 'right',
                      thickness: 20,
                      len: 0.8
                    },
                    hovertemplate: 
                      '<b>Element:</b> %{x}<br>' +
                      '<b>Property:</b> %{y} (<span style="font-size:12px">YS=Yield Strength, UTS=Ultimate Strength, EL=Elongation, DS=Density</span>)<br>' +
                      '<b>Correlation:</b> %{z:.3f}<br>' +
                      '<extra></extra>'
                  }]}
                  layout={{
                    title: (() => {
                      const modeLabels = {
                        'occurrence': 'Element-Property Correlation Matrix (Element Occurrence)',
                        'atomic': 'Element-Property Correlation Matrix (Atomic Fraction)',
                        'weight': 'Element-Property Correlation Matrix (Weight Fraction)'
                      };
                      return modeLabels[propertyAnalysisMode] || 'Element-Property Correlation Matrix';
                    })(),
                    autosize: true,
                    width: null,
                    height: null,
                    margin: { t: 50, b: 50, l: 50, r: 50 },
                    xaxis: {
                      title: 'Elements',
                      tickangle: -45,
                      uirevision: 'x-axis'
                    },
                    yaxis: {
                      title: 'Properties',
                      uirevision: 'y-axis'
                    },
                    dragmode: 'zoom',
                    uirevision: 'element-property-correlation'
                  }}
                  useResizeHandler
                  style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
                  config={{ 
                    responsive: true,
                    displayModeBar: true,
                    displaylogo: false,
                    modeBarButtonsToAdd: ['zoom2d', 'pan2d', 'zoomIn2d', 'zoomOut2d', 'resetScale2d'],
                    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
                    scrollZoom: false
                  }}
                />
              </div>
            ) : (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '400px',
                color: '#666',
                fontSize: '16px'
              }}>
                No correlation data available
              </div>
            )}
          </div>

          {/* 说明文本 */}
          <div style={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#444'
          }}>
            <div style={{ fontWeight: '500', marginBottom: '8px' }}>Description:</div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><b>YS</b> = Yield Strength, <b>UTS</b> = Ultimate Strength, <b>EL</b> = Elongation, <b>DS</b> = Density</li>
              <li>This chart shows the correlation between element content and material properties using different analysis modes.</li>
              <li><b>Analysis Modes:</b>
                <ul>
                  <li><b>Occurrence (0/1):</b> Binary relationship - whether elements are present (1) or absent (0)</li>
                  <li><b>Atomic Fraction:</b> Correlation based on atomic fractions of elements</li>
                  <li><b>Weight Fraction:</b> Correlation based on weight fractions of elements</li>
                </ul>
              </li>
              <li>The correlation coefficient ranges from -1 to 1, where:
                <ul>
                  <li>1 indicates a perfect positive correlation</li>
                  <li>-1 indicates a perfect negative correlation</li>
                  <li>0 indicates no correlation</li>
                </ul>
              </li>
              <li>You can adjust the following parameters:
                <ul>
                  <li>Analysis Mode: Choose how element content is calculated for correlation analysis</li>
                  <li>Correlation Method: Choose between Pearson, Spearman, or Kendall correlation</li>
                  <li>Element Frequency Range: Filter elements based on their occurrence frequency</li>
                  <li>Property Ranges: Adjust the ranges for yield strength, ultimate strength, elongation, and density</li>
                </ul>
              </li>
              <li>Only elements with sufficient data points within all specified ranges are included in the analysis.</li>
            </ul>
          </div>
        </div>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            fontSize: '18px',
            color: '#666',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #eaeaea'
          }}>
            Loading Element Co-occurrence Network...
          </div>
        }>
          <ElementCooccurrenceTab 
            data={data}
            loading={loading}
            selectedMainGroups={selectedMainGroups}
          />
        </Suspense>
      </TabPanel>

      {/* 新增特征分析Tab */}
      <TabPanel value={tabValue} index={6}>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            fontSize: '18px',
            color: '#666',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #eaeaea'
          }}>
            正在加载特征分析...
          </div>
        }>
          <FeatureAnalysisTab data={data} loading={loading} selectedMainGroups={selectedMainGroups} />
        </Suspense>
      </TabPanel>

      {/* 新增仿真数据Tab */}
      <TabPanel value={tabValue} index={7}>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            fontSize: '18px',
            color: '#666',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #eaeaea'
          }}>
            Loading Simulation Data Analysis...
          </div>
        }>
          <SimulationDataTab onNavigate={onNavigate} />
        </Suspense>
      </TabPanel>
    </div>
  );
}

export default DataPage;
